// @file All network requests used by dashboard store
import type { UserReason } from '@@/bits/account_setup_helper'
import { apiUrlInterpolate } from '@@/bits/api_url_interpolate'
import getCsrfToken from '@@/bits/csrf_token'
import { asciiSafeStringify } from '@@/bits/json_stringify'
import { transformCurrentUrl } from '@@/bits/location'
import { ApiErrorCode, HttpCode, type LibraryMembershipRole, type LibraryMembershipStatus } from '@@/enums'
import type { LearnVideo } from '@@/pinia/dash_learn_store'
import { fetchJson } from '@@/surface/api_fetch'
import type {
  AiMakeSuggestion,
  AssignmentComment,
  AssignmentCommentId,
  AssignmentsListPage,
  AssignmentState,
  AttachmentTypeSettings as AttachmentTypeSettingsPayload,
  BackpackPlanResult,
  CampusAssignment,
  CampusAssignmentId,
  CampusDiscussionId,
  ChangeCardUrl,
  ClassInformation as ClassInformationType,
  ContentSafetySettings as ContentSafetySettingsPayload,
  ContentSafetySettingsUpdateApiRequest,
  DomainValidation,
  EmailAddressVerificationResult,
  EstimateLibrarySwitchingCostResult,
  EstimateTeamNeonDowngradeRefund,
  Folder as FolderType,
  FolderApiResponse,
  FolderId,
  GoogleAppLicensingCreateResponse,
  GoogleAppLicensingSettingsResponse,
  GoogleAppLicensingValidateResponse,
  GoogleClassroomCourse,
  Id,
  JsonApiData,
  JsonApiResponse,
  Library as LibraryType,
  LibraryAnalytics as LibraryAnalyticsType,
  LibraryAnalyticsMetricName,
  LibraryAnalyticsMetrics as LibraryAnalyticsMetricsType,
  LibraryAttributeCheck,
  LibraryDowngradeToTeamNeonResult,
  LibraryId,
  LibraryInvitedUser,
  LibraryInvitedUserValidationResult,
  LibraryInviteLinks,
  LibraryInvoice as LibraryInvoiceType,
  LibraryMembership,
  LibraryMembershipId,
  LibraryOauthSettings as LibraryOauthSettingsType,
  LibraryPendingInvitation,
  LibrarySwitchResult,
  LibraryTimePeriod,
  LibraryUserEmailDomain as LibraryUserEmailDomainType,
  LibraryUsersQuery,
  NotificationSettingApiResponse,
  PaymentStatusResponse,
  PersonalToLibraryTransferWallsResponse,
  SamlConnectionApiResponse,
  SchoolLibraryPermission,
  SelfServeTrialExperimentEnrollmentResponse,
  TeacherSchool as TeacherSchoolType,
  TeacherSchoolProfile as TeacherSchoolProfileType,
  TeamLibraryPermission,
  Tenant as TenantType,
  TenantAnalytics as TenantAnalyticsType,
  TenantId,
  TenantInviteLinks as TenantInviteLinksType,
  TenantInviteLinksParams,
  TenantParams,
  UndoLibraryScheduledChangeResult,
  UpdateUserGroupAttributes,
  User as UserType,
  UserAchievements as UserAchievementsType,
  UserAchievementsAnalyticsWallContributions as UserAchievementsAnalyticsWallContributionsType,
  UserCamelCase,
  UserFollowApiResponse,
  UserGroupDetailsApiResponse,
  UserGroupFolderApiResponse,
  UserGroupMemberApiResponse,
  UserId,
  UserLibrarySetting,
  UserLibrarySettingsApiResponse,
  VerificationCodePurpose,
  WallFollowApiResponse,
  WallGalleryTemplate,
  WallId,
  WallSuggestedTemplatesData,
} from '@@/types'
import type { Invoice as InvoiceType, JsonAPIResponse, JsonAPIUpdateRequest, Wall as WallType } from '@padlet/arvo'
import type { FetchOptions } from '@padlet/fetch'
import { fetchResponse, HTTPMethod } from '@padlet/fetch'

// Ensure this version is synced with `mozart/tests/playwright/helpers/dashboard_test.ts`
const API_VERSION = 5

class Invoice {
  public static async fetch(): Promise<JsonAPIResponse<InvoiceType>> {
    return await fetchJson(`/api/11/invoices`, {
      method: HTTPMethod.get,
    })
  }
}

class LibraryPermissions {
  public static async fetch(id: LibraryId): Promise<JsonAPIResponse<SchoolLibraryPermission | TeamLibraryPermission>> {
    return await fetchJson(`/api/1/libraries/${id}/library-settings`, {
      method: HTTPMethod.get,
    })
  }

  public static async update(
    id: LibraryId,
    librarySettings,
  ): Promise<JsonAPIResponse<SchoolLibraryPermission | TeamLibraryPermission>> {
    return await fetchJson(`/api/1/libraries/${id}/library-settings`, {
      method: HTTPMethod.patch,
      body: asciiSafeStringify({ data: { attributes: { settings: librarySettings } } }),
    })
  }

  public static async updateMemberLoginOtp(
    id: LibraryId,
    required: boolean,
  ): Promise<JsonAPIResponse<SchoolLibraryPermission | TeamLibraryPermission>> {
    return await fetchJson(`/api/1/libraries/${id}/library-settings`, {
      method: HTTPMethod.patch,
      body: asciiSafeStringify({ data: { attributes: { requireMemberLoginOtp: required } } }),
    })
  }
}

class LibraryBilling {
  /**
   * Fetch library billing information.
   */
  public static async fetch({ libraryId }: { libraryId: LibraryId }, fetchOptions = {}): Promise<JsonAPIResponse<any>> {
    return await fetchJson(`/api/${API_VERSION}/libraries/${libraryId}/billing`, {
      method: HTTPMethod.get,
      ...fetchOptions,
    })
  }

  /**
   * Cancel library subscription.
   */
  public static async cancelSubscription(
    { libraryId }: { libraryId: LibraryId },
    fetchOptions = {},
  ): Promise<JsonAPIResponse<LibraryDowngradeToTeamNeonResult>> {
    return await fetchJson(`/api/${API_VERSION}/libraries/${libraryId}/billing/cancel`, {
      method: HTTPMethod.post,
      ...fetchOptions,
    })
  }

  /**
   * Fetch estimated downgrade to Team Neon refund.
   */
  public static async estimateTeamNeonDowngradeRefund(
    { libraryId }: { libraryId: LibraryId },
    fetchOptions = {},
  ): Promise<JsonAPIResponse<EstimateTeamNeonDowngradeRefund>> {
    return await fetchJson(`/api/${API_VERSION}/libraries/${libraryId}/billing/cancel/refund_estimate`, {
      method: HTTPMethod.get,
      ...fetchOptions,
    })
  }

  /**
   * Fetch estimated downgrade to Team Neon refund.
   */
  public static async libraryChangeCardUrl(
    { libraryId }: { libraryId: LibraryId },
    fetchOptions = {},
  ): Promise<JsonAPIResponse<ChangeCardUrl>> {
    return await fetchJson(`/api/${API_VERSION}/libraries/${libraryId}/billing/change_card_url`, {
      method: HTTPMethod.get,
      ...fetchOptions,
    })
  }

  /**
   * Undo a scheduled downgrade to Team Neon.
   */
  public static async undoScheduledDowngradeToTeamNeon(
    { libraryId }: { libraryId: LibraryId },
    fetchOptions = {},
  ): Promise<JsonAPIResponse<UndoLibraryScheduledChangeResult>> {
    return await fetchJson(`/api/${API_VERSION}/libraries/${libraryId}/billing/undo_scheduled_cancellation`, {
      method: HTTPMethod.post,
      ...fetchOptions,
    })
  }

  /**
   * Undo a scheduled crossgrade.
   */
  public static async undoScheduledCrossgrade(
    { libraryId }: { libraryId: LibraryId },
    fetchOptions = {},
  ): Promise<JsonAPIResponse<UndoLibraryScheduledChangeResult>> {
    return await fetchJson(`/api/${API_VERSION}/libraries/${libraryId}/billing/undo_scheduled_plan_switch`, {
      method: HTTPMethod.post,
      ...fetchOptions,
    })
  }

  public static async estimateSwitchingCost(
    { libraryId }: { libraryId: LibraryId },
    fetchOptions = {},
  ): Promise<JsonAPIResponse<EstimateLibrarySwitchingCostResult>> {
    return await fetchJson(`/api/${API_VERSION}/libraries/${libraryId}/billing/switch_plan/estimate_cost`, {
      method: HTTPMethod.post,
      ...fetchOptions,
    })
  }

  public static async estimateClassroomSwitchingCost(
    { libraryId }: { libraryId: LibraryId },
    fetchOptions = {},
  ): Promise<JsonAPIResponse<EstimateLibrarySwitchingCostResult>> {
    return await fetchJson(`/api/${API_VERSION}/libraries/${libraryId}/billing/switch-classroom-plan/estimate-cost`, {
      method: HTTPMethod.post,
      ...fetchOptions,
    })
  }

  public static async switchClassroomPlan(
    { libraryId }: { libraryId: LibraryId },
    fetchOptions = {},
  ): Promise<JsonAPIResponse<LibrarySwitchResult>> {
    return await fetchJson(`/api/${API_VERSION}/libraries/${libraryId}/billing/switch-classroom-plan`, {
      method: HTTPMethod.post,
      ...fetchOptions,
    })
  }

  public static async switchPlan(
    { libraryId }: { libraryId: LibraryId },
    fetchOptions = {},
  ): Promise<JsonAPIResponse<LibrarySwitchResult>> {
    return await fetchJson(`/api/${API_VERSION}/libraries/${libraryId}/billing/switch_plan`, {
      method: HTTPMethod.post,
      ...fetchOptions,
    })
  }

  public static async estimateLibraryPlanQuantityChange(
    { libraryId, newPlanQuantity }: { libraryId: LibraryId; newPlanQuantity: number },
    fetchOptions = {},
  ): Promise<JsonAPIResponse<EstimateLibrarySwitchingCostResult>> {
    return await fetchJson(`/api/${API_VERSION}/libraries/${libraryId}/billing/switch-plan-quantity/estimate-cost`, {
      method: HTTPMethod.post,
      jsonData: {
        quantity: newPlanQuantity,
      },
      ...fetchOptions,
    })
  }

  public static async updatePlanQuantity(
    { libraryId, newPlanQuantity }: { libraryId: LibraryId; newPlanQuantity: number },
    fetchOptions = {},
  ): Promise<JsonAPIResponse<any>> {
    return await fetchJson(`/api/${API_VERSION}/libraries/${libraryId}/billing/update-plan-quantity`, {
      method: HTTPMethod.post,
      jsonData: {
        quantity: newPlanQuantity,
      },
      ...fetchOptions,
    })
  }

  public static async undoScheduledPlanQuantitySwitch(
    { libraryId }: { libraryId: LibraryId },
    fetchOptions = {},
  ): Promise<JsonAPIResponse<any>> {
    return await fetchJson(`/api/${API_VERSION}/libraries/${libraryId}/billing/undo-scheduled-plan-quantity-switch`, {
      method: HTTPMethod.post,
      ...fetchOptions,
    })
  }
}

class LibraryInvoice {
  public static async fetch(
    { libraryId }: { libraryId: LibraryId },
    fetchOptions = {},
  ): Promise<JsonAPIResponse<LibraryInvoiceType>> {
    return await fetchJson(`/api/11/libraries/${libraryId}/billing/invoices`, {
      method: HTTPMethod.get,
      ...fetchOptions,
    })
  }
}

class Library {
  public static get libraryUrl(): string {
    return `/api/1/libraries?userId=\${ userId }&filter=\${ filter }`
  }

  public static buildUrl(url, options): string {
    return apiUrlInterpolate(url, options)
  }

  public static async fetchCreatableLibraries(
    { userId }: { userId: UserId },
    fetchOptions = {},
  ): Promise<JsonAPIResponse<LibraryType>> {
    return await fetchJson(this.buildUrl(this.libraryUrl, { userId, filter: 'wall_creatable' }), {
      method: HTTPMethod.get,
      ...fetchOptions,
    })
  }

  public static async fetchCreatableAndVisibleLibraries(
    { userId }: { userId: UserId },
    fetchOptions = {},
  ): Promise<JsonAPIResponse<LibraryType>> {
    return await fetchJson(this.buildUrl(this.libraryUrl, { userId, filter: 'wall_creatable_and_visible' }), {
      method: HTTPMethod.get,
      ...fetchOptions,
    })
  }

  public static async fetchViewableLibraries(
    { userId }: { userId: UserId },
    fetchOptions = {},
  ): Promise<JsonAPIResponse<LibraryType>> {
    return await fetchJson(this.buildUrl(this.libraryUrl, { userId, filter: 'all_walls_viewable' }), {
      method: HTTPMethod.get,
      ...fetchOptions,
    })
  }

  public static async fetchViewableAndVisibleLibraries(
    { userId }: { userId: UserId },
    fetchOptions = {},
  ): Promise<JsonAPIResponse<LibraryType>> {
    return await fetchJson(this.buildUrl(this.libraryUrl, { userId, filter: 'all_walls_viewable_and_visible' }), {
      method: HTTPMethod.get,
      ...fetchOptions,
    })
  }

  public static async delete(
    { libraryId }: { libraryId: LibraryId },
    fetchOptions = {},
  ): Promise<JsonAPIResponse<LibraryType>> {
    return await fetchJson(`/api/${API_VERSION}/libraries/${libraryId}`, {
      method: HTTPMethod.delete,
      ...fetchOptions,
    })
  }

  public static async checkNameAndSlug(name: string, slug: string): Promise<JsonAPIResponse<LibraryAttributeCheck>> {
    return await fetchJson(`/api/1/libraries/check-name-and-slug`, {
      method: HTTPMethod.get,
      query: { name, slug },
    })
  }
}

class LibraryInfo {
  public static async fetch(
    { libraryId }: { libraryId: LibraryId },
    fetchOptions = {},
  ): Promise<JsonAPIResponse<LibraryType>> {
    return await fetchJson(`/api/1/libraries/${libraryId}/info`, {
      method: HTTPMethod.get,
      ...fetchOptions,
    })
  }

  public static async update(
    {
      libraryId,
      updateAttributes,
    }: { libraryId: LibraryId; updateAttributes: { avatar?: string; name?: string; slug?: string; bio?: string } },
    fetchOptions = {},
  ): Promise<JsonAPIResponse<LibraryType>> {
    return await fetchJson(`/api/1/libraries/${libraryId}/info`, {
      method: HTTPMethod.patch,
      jsonData: {
        data: {
          attributes: updateAttributes,
        },
      },
      ...fetchOptions,
    })
  }
}

class GoogleClassroom {
  public static async updateLibraryAdminEmail(
    { libraryId, email }: { libraryId: LibraryId; email: string },
    fetchOptions = {},
  ): Promise<JsonAPIResponse<LibraryType>> {
    return await fetchJson(`/api/google-classroom/settings/admin?libraryId=${libraryId}`, {
      method: HTTPMethod.patch,
      jsonData: {
        email,
      },
      ...fetchOptions,
    })
  }

  public static async removeLibraryAdminEmail(
    { libraryId }: { libraryId: LibraryId },
    _fetchOptions = {},
  ): Promise<JsonAPIResponse<LibraryType>> {
    return await fetchJson(`/api/google-classroom/settings/admin?libraryId=${libraryId}`, {
      method: HTTPMethod.delete,
    })
  }

  public static async admin_courses(
    { libraryId, fromRemote }: { libraryId: LibraryId; fromRemote?: boolean },
    fetchOptions = {},
  ): Promise<JsonAPIResponse<GoogleClassroomCourse[]>> {
    return await fetchJson(
      `/api/google-classroom/settings/admin-courses?libraryId=${libraryId}&fromRemote=${fromRemote}`,
      {
        method: HTTPMethod.get,
        ...fetchOptions,
      },
    )
  }
}

class GoogleWorkspace {
  public static async updateLibraryAdminEmail(
    { libraryId, email }: { libraryId: LibraryId; email: string },
    fetchOptions = {},
  ): Promise<JsonAPIResponse<LibraryType>> {
    return await fetchJson(`/api/google-workspace/settings/admin?libraryId=${libraryId}`, {
      method: HTTPMethod.patch,
      jsonData: {
        email,
      },
      ...fetchOptions,
    })
  }
  public static async removeLibraryAdminEmail(
    { libraryId }: { libraryId: LibraryId },
    fetchOptions = {},
  ): Promise<JsonAPIResponse<LibraryType>> {
    return await fetchJson(`/api/google-workspace/settings/admin?libraryId=${libraryId}`, {
      method: HTTPMethod.delete,
      ...fetchOptions,
    })
  }

  public static async updateClassroomRostering(
    { libraryId, enabled }: { libraryId: LibraryId; enabled: boolean },
    fetchOptions = {},
  ): Promise<JsonAPIResponse<{ meta: { success: boolean } }>> {
    return await fetchJson(`/api/google-workspace/settings/classroom-rostering?libraryId=${libraryId}`, {
      method: HTTPMethod.patch,
      jsonData: {
        enabled,
      },
      ...fetchOptions,
    })
  }
}

class LibraryUserEmailDomain {
  public static async fetch(
    { libraryId }: { libraryId: LibraryId },
    fetchOptions = {},
  ): Promise<JsonAPIResponse<LibraryUserEmailDomainType>> {
    return await fetchJson(`/api/1/libraries/${libraryId}/user_domains`, {
      method: HTTPMethod.get,
      ...fetchOptions,
    })
  }

  public static async requestApproval(
    { libraryId, domains }: { libraryId: LibraryId; domains: Array<Partial<LibraryUserEmailDomainType>> },
    fetchOptions = {},
  ): Promise<JsonAPIResponse<LibraryUserEmailDomainType>> {
    return await fetchJson(`/api/1/libraries/${libraryId}/user-domains/request-approval`, {
      method: HTTPMethod.post,
      jsonData: {
        domains,
      },
      ...fetchOptions,
    })
  }

  public static async delete(
    { libraryId, domainName }: { libraryId: LibraryId; domainName: string },
    fetchOptions = {},
  ): Promise<JsonAPIResponse<LibraryUserEmailDomainType>> {
    return await fetchJson(`/api/1/libraries/${libraryId}/user-domains`, {
      method: HTTPMethod.delete,
      jsonData: {
        domain_name: domainName,
      },
      ...fetchOptions,
    })
  }
}

class LibraryOauthSettings {
  public static async fetch(
    { libraryId }: { libraryId: LibraryId },
    fetchOptions = {},
  ): Promise<JsonAPIResponse<LibraryOauthSettingsType>> {
    return await fetchJson(`/api/1/libraries/${libraryId}/oauth-settings`, {
      method: HTTPMethod.get,
      ...fetchOptions,
    })
  }

  public static async update(
    {
      libraryId,
      oauthMethod,
      enable,
      thirdPartyIdInput,
    }: { libraryId: LibraryId; oauthMethod: string; enable: boolean; thirdPartyIdInput?: string },
    fetchOptions = {},
  ): Promise<JsonAPIResponse<LibraryOauthSettingsType>> {
    return await fetchJson(`/api/1/libraries/${libraryId}/oauth-settings`, {
      method: HTTPMethod.patch,
      jsonData: {
        oauthProvider: oauthMethod,
        enable,
        thirdPartyId: thirdPartyIdInput,
      },
      ...fetchOptions,
    })
  }

  public static async fetchGoogleAppLicensing(
    { libraryId }: { libraryId: LibraryId },
    fetchOptions = {},
  ): Promise<JsonAPIResponse<GoogleAppLicensingSettingsResponse>> {
    return await fetchJson(`/api/1/libraries/${libraryId}/google-app-licensing-settings`, {
      method: HTTPMethod.get,
      ...fetchOptions,
    })
  }

  public static async updateGoogleAppLicensing(
    {
      libraryId,
      enable,
      orderId,
      redeemUri,
    }: { libraryId: LibraryId; enable: boolean; orderId?: string; redeemUri?: string },
    fetchOptions = {},
  ): Promise<JsonAPIResponse<LibraryOauthSettingsType>> {
    return await fetchJson(`/api/1/libraries/${libraryId}/google-app-licensing-settings`, {
      method: HTTPMethod.patch,
      jsonData: {
        enable,
        order_id: orderId,
        redeem_uri: redeemUri,
      },
      ...fetchOptions,
    })
  }
}

class LibraryMembers {
  public static async fetchMemberships(
    { libraryId, next }: { libraryId: LibraryId; next: string },
    fetchOptions = {},
  ): Promise<JsonAPIResponse<LibraryMembership>> {
    const url = transformCurrentUrl(
      {},
      {
        path: '/api/1/libraries/memberships',
        search: {
          library_id: String(libraryId),
          next,
        },
      },
    )
    return await fetchJson(url, {
      method: HTTPMethod.get,
      ...fetchOptions,
    })
  }

  public static async updateMembership(
    { id, role, status }: { id: LibraryMembershipId; role?: LibraryMembershipRole; status?: LibraryMembershipStatus },
    fetchOptions = {},
  ): Promise<JsonAPIResponse<LibraryMembership>> {
    return await fetchJson('/api/1/libraries/memberships', {
      method: HTTPMethod.patch,
      jsonData: {
        data: {
          attributes: {
            id,
            role,
            status,
          },
        },
      },
      ...fetchOptions,
    })
  }

  public static async updateMemberInfo(
    {
      id,
      name,
      username,
      avatar,
      password,
    }: { id: LibraryMembershipId; name?: string; username?: string; avatar?: string; password?: string },
    fetchOptions = {},
  ): Promise<JsonAPIResponse<LibraryMembership>> {
    return await fetchJson(`/api/1/libraries/memberships/${id}/manage`, {
      method: HTTPMethod.patch,
      jsonData: {
        name,
        username,
        avatar,
        password,
      },
      ...fetchOptions,
    })
  }

  public static async requestPasswordReset({ id }: { id: LibraryMembershipId }, fetchOptions = {}): Promise<void> {
    await fetchJson(`/api/1/libraries/memberships/${id}/request-password-reset`, {
      method: HTTPMethod.post,
      ...fetchOptions,
    })
  }

  public static async changeOwner(
    { libraryId, userId }: { libraryId: LibraryId; userId: UserId },
    fetchOptions = {},
  ): Promise<void> {
    return await fetchJson(`/api/1/libraries/${libraryId}/change_owner`, {
      method: HTTPMethod.post,
      jsonData: {
        userId,
      },
      ...fetchOptions,
    })
  }

  public static async leaveLibrary({ libraryId }: { libraryId: LibraryId }, fetchOptions = {}): Promise<void> {
    return await fetchJson('/api/1/libraries/leave', {
      method: HTTPMethod.post,
      jsonData: {
        libraryId,
      },
      ...fetchOptions,
    })
  }

  public static async fetchLibraryInviteLinks(
    { libraryId }: { libraryId: LibraryId },
    fetchOptions = {},
  ): Promise<JsonAPIResponse<LibraryInviteLinks>> {
    return await fetchJson(`/api/1/libraries/invite_links?library_id=${libraryId}`, {
      method: HTTPMethod.get,
      ...fetchOptions,
    })
  }

  public static async toggleInviteLink(
    updateAttributes: {
      libraryId: LibraryId
      codeForAdminEnabled?: boolean
      codeForMakerEnabled?: boolean
      codeForContributorEnabled?: boolean
    },
    fetchOptions = {},
  ): Promise<JsonAPIResponse<LibraryInviteLinks>> {
    return await fetchJson('/api/1/libraries/invite-links', {
      method: HTTPMethod.patch,
      jsonData: {
        data: {
          attributes: updateAttributes,
        },
      },
      ...fetchOptions,
    })
  }

  public static async search(
    {
      libraryId,
      query,
      next,
    }: {
      libraryId: LibraryId
      query: string
      next: string
    },
    fetchOptions = {},
  ): Promise<JsonAPIResponse<LibraryMembership>> {
    const url = transformCurrentUrl(
      {},
      {
        path: '/api/1/libraries/memberships/search',
        search: {
          library_id: String(libraryId),
          q: query,
          next,
        },
      },
    )
    return await fetchJson(url, {
      method: HTTPMethod.get,
      ...fetchOptions,
    })
  }

  public static async sendEmailInvites(
    { libraryId, emailRows }: { libraryId: LibraryId; emailRows: Array<{ email: string; role: string }> },
    fetchOptions = {},
  ): Promise<{ status: string; duplicate_user_count?: number }> {
    return await fetchJson(`/api/1/libraries/${libraryId}/memberships/send-email-invites`, {
      method: HTTPMethod.post,
      jsonData: {
        data: {
          attributes: {
            invited_users: emailRows,
          },
        },
      },
      ...fetchOptions,
    })
  }

  public static async resendEmailInvites(
    { libraryId, emails }: { libraryId: LibraryId; emails: string[] },
    fetchOptions = {},
  ): Promise<{ status: string }> {
    return await fetchJson(`/api/1/libraries/${libraryId}/memberships/resend-email-invites`, {
      method: HTTPMethod.post,
      jsonData: {
        data: {
          attributes: {
            emails,
          },
        },
      },
      ...fetchOptions,
    })
  }

  public static async removeEmailInvites(
    { libraryId, emails }: { libraryId: LibraryId; emails: string[] },
    fetchOptions = {},
  ): Promise<{ status: string }> {
    return await fetchJson(`/api/1/libraries/${libraryId}/memberships/remove-email-invites`, {
      method: HTTPMethod.delete,
      jsonData: {
        data: {
          attributes: {
            emails,
          },
        },
      },
      ...fetchOptions,
    })
  }

  public static async csvBulkAddMembers(
    { libraryId, InvitedUsersList }: { libraryId: LibraryId; InvitedUsersList: LibraryInvitedUser[] },
    fetchOptions = {},
  ): Promise<{
    status: string
    emailless_users_processed: Array<{ name: string; username?: string; status: string }>
    memberships: { data: LibraryMembership[] }
    duplicate_user_count?: number
  }> {
    return await fetchJson(`/api/1/libraries/${libraryId}/memberships/csv-bulk-add`, {
      method: HTTPMethod.post,
      jsonData: {
        data: {
          attributes: {
            invited_users: InvitedUsersList,
          },
        },
      },
      ...fetchOptions,
    })
  }

  public static async validateCsv(
    { libraryId, invitedUsersList }: { libraryId: LibraryId; invitedUsersList: LibraryInvitedUser[] },
    fetchOptions = {},
  ): Promise<JsonAPIResponse<LibraryInvitedUserValidationResult[]>> {
    return await fetchJson(`/api/1/libraries/${libraryId}/memberships/csv-bulk-add/validate`, {
      method: HTTPMethod.post,
      jsonData: {
        data: {
          attributes: {
            invited_users: invitedUsersList,
          },
        },
      },
      ...fetchOptions,
    })
  }

  public static async fetchInvitedUsers(
    { libraryId }: { libraryId: LibraryId },
    fetchOptions = {},
  ): Promise<{ data: LibraryPendingInvitation[] }> {
    const url = transformCurrentUrl(
      {},
      {
        path: `/api/1/libraries/memberships/invited-users`,
        searchParams: { library_id: libraryId },
      },
    )
    return await fetchJson(url, {
      method: HTTPMethod.get,
      ...fetchOptions,
    })
  }
}

class MicrosoftTeams {
  public static async unlink({ libraryId }: { libraryId: LibraryId }): Promise<{ ok: boolean }> {
    return await fetchJson(`/api/microsoft-teams/unlink`, {
      method: HTTPMethod.post,
      jsonData: { libraryId },
    })
  }

  public static async fetchUserPermissions(params: {
    tenantId: string
    userId: string
    ssoToken: string
    userEmail: string
  }) {
    return await fetchJson('/api/microsoft-teams/request-permissions', {
      method: HTTPMethod.post,
      jsonData: params,
    })
  }

  public static async completePersonalLinking(params: {
    tenantId: string
    userId: string
    configureToken: string
    ssoToken: string
  }): Promise<{ ok: boolean }> {
    return await fetchJson('/api/microsoft-teams/complete-personal-linking', {
      method: HTTPMethod.post,
      jsonData: params,
    })
  }

  public static async fetchDeepLinkMenu(params: {
    tenantId: string
    userId: string
    teamId?: string
    channelId?: string
    userDisplayName?: string
    userEmail?: string
    userTeamsRole?: string
    ssoToken: string
  }): Promise<{
    userDisplayName: string
    username: string
    avatar: string
    libraryName: string
    walls: unknown[]
    createNewPadletUrl: string
    openDashboardUrl: string
    padletUserEmail: string
    linkedToLibrary: boolean
  }> {
    return await fetchJson('/api/microsoft-teams/deep-link-menu', {
      method: HTTPMethod.post,
      jsonData: params,
    })
  }

  public static async logoutPersonal({ teamsUserId }: { teamsUserId: string }): Promise<{ success: boolean }> {
    return await fetchJson('/api/microsoft-teams/logout-personal', {
      method: HTTPMethod.post,
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      jsonData: {
        teamsUserId,
      },
    })
  }
}

class BackpackPlan {
  /**
   * Fetch list of available backpack plans for current user (respective to the user's country code/currency)
   */
  public static async fetch(fetchOptions = {}): Promise<JsonAPIResponse<BackpackPlanResult>> {
    return await fetchJson(`/api/${API_VERSION}/backpack_plans`, {
      method: HTTPMethod.get,
      ...fetchOptions,
    })
  }
}

class User {
  public static async sendResetPasswordEmail(fetchOptions = {}): Promise<JsonAPIResponse<any>> {
    return await fetchJson('/api/1/send_reset_password', {
      method: HTTPMethod.post,
      ...fetchOptions,
    })
  }

  public static async updatePassword(fetchOptions = {}): Promise<JsonAPIResponse<UserType>> {
    return await fetchJson(`/api/1/dashboard_settings/password`, {
      method: HTTPMethod.put,
      ...fetchOptions,
    })
  }

  public static async fetchSettingsUserInfo(fetchOptions = {}): Promise<JsonAPIResponse<UserCamelCase>> {
    return await fetchJson(`/api/1/dashboard_settings/user`, {
      method: HTTPMethod.get,
      ...fetchOptions,
    })
  }

  public static async updateSettingsUserInfo(fetchOptions = {}): Promise<JsonAPIResponse<UserType>> {
    return await fetchJson(`/api/1/dashboard_settings/user`, {
      method: HTTPMethod.patch,
      ...fetchOptions,
    })
  }

  public static async removeGoogleDriveAuthorization(id: UserId, fetchOptions = {}): Promise<object> {
    return await fetchJson(`/api/1/user/${id}/user_integrations/google_drive`, {
      method: HTTPMethod.delete,
      ...fetchOptions,
    })
  }

  public static async removeMicrosoftOneDriveAuthorization(id: UserId, fetchOptions = {}): Promise<object> {
    return await fetchJson(`/api/1/user/${id}/user-integrations/microsoft-onedrive`, {
      method: HTTPMethod.delete,
      ...fetchOptions,
    })
  }

  public static async updateUserReason(
    id: UserId,
    reason: UserReason,
  ): Promise<JsonAPIResponse<{ reason: UserReason }>> {
    return await fetchJson(`/api/1/users/${id}/reason_for_using_padlet`, {
      method: HTTPMethod.patch,
      body: asciiSafeStringify({ data: { attributes: { reason } } }),
    })
  }

  public static async updateBasicInfo(id: UserId, fetchOptions = {}): Promise<UserType> {
    return await fetchJson(`/users/${id}`, {
      method: HTTPMethod.put,
      ...fetchOptions,
    })
  }

  public static async resetPassword(id: UserId, token: string, newPassword: string): Promise<UserType> {
    return await fetchJson(`/users/${id}/change_password`, {
      method: HTTPMethod.put,
      body: asciiSafeStringify({ token, new_password: newPassword }),
    })
  }

  public static async requestResetPassword(payload: { email?: string; username?: string }): Promise<UserType> {
    return await fetchJson(`/api/${API_VERSION}/request-reset-password`, {
      method: HTTPMethod.post,
      body: asciiSafeStringify(payload.email != null ? { email: payload.email } : { username: payload.username }),
    })
  }

  public static async getApiToken(fetchOptions = {}): Promise<JsonAPIResponse<any>> {
    return await fetchJson(`/api/1/public_api/tokens`, {
      method: HTTPMethod.get,
      ...fetchOptions,
    })
  }

  public static async generateApiToken(fetchOptions = {}): Promise<JsonAPIResponse<any>> {
    return await fetchJson(`/api/1/public_api/tokens`, {
      method: HTTPMethod.post,
      ...fetchOptions,
    })
  }

  public static async refreshApiToken(token: string | null): Promise<JsonAPIResponse<any>> {
    return await fetchJson(`/api/1/public_api/tokens/refresh`, {
      method: HTTPMethod.post,
      body: asciiSafeStringify({ token }),
    })
  }

  public static async canIAccessApi(fetchOptions = {}): Promise<JsonAPIResponse<any>> {
    return await fetchJson(`/api/1/public_api/auth`, {
      method: HTTPMethod.get,
      ...fetchOptions,
    })
  }

  public static async getZapierApiToken(fetchOptions = {}): Promise<JsonAPIResponse<any>> {
    return await fetchJson(`/api/1/public-api/zapier-tokens`, {
      method: HTTPMethod.get,
      ...fetchOptions,
    })
  }

  public static async generateZapierApiToken(fetchOptions = {}): Promise<JsonAPIResponse<any>> {
    return await fetchJson(`/api/1/public-api/zapier-tokens`, {
      method: HTTPMethod.post,
      ...fetchOptions,
    })
  }

  public static async refreshZapierApiToken(token: string | null): Promise<JsonAPIResponse<any>> {
    return await fetchJson(`/api/1/public-api/zapier-tokens/refresh`, {
      method: HTTPMethod.post,
      body: asciiSafeStringify({ token }),
    })
  }

  public static async revokeZapierApiToken(token: string | null): Promise<JsonAPIResponse<any>> {
    return await fetchJson(`/api/1/public-api/zapier-tokens/revoke`, {
      method: HTTPMethod.post,
      body: asciiSafeStringify({ token }),
    })
  }

  public static async fetchUserLibrarySettings(): Promise<JsonAPIResponse<UserLibrarySettingsApiResponse>> {
    return await fetchJson('/api/1/dashboard_settings/user_library_settings', {
      method: HTTPMethod.get,
    })
  }

  public static async updateUserLibrarySetting({
    libraryId,
    visible,
  }: {
    libraryId: UserLibrarySetting['libraryId']
    visible: UserLibrarySetting['visible']
  }): Promise<JsonAPIResponse<UserLibrarySettingsApiResponse>> {
    return await fetchJson('/api/1/dashboard_settings/user_library_settings', {
      method: HTTPMethod.patch,
      body: asciiSafeStringify({ data: { attributes: { librarySetting: { libraryId, visible } } } }),
    })
  }

  public static async updateUserDefaultLibrary(
    defaultLibrary: UserLibrarySetting['libraryId'],
  ): Promise<JsonAPIResponse<UserLibrarySettingsApiResponse>> {
    return await fetchJson('/api/1/dashboard_settings/user_library_settings/default_library', {
      method: HTTPMethod.patch,
      body: asciiSafeStringify({ data: { attributes: { defaultLibrary } } }),
    })
  }

  public static async updateUserFeatureFlags(params: {
    userToggleKey: string
    userToggleValue: boolean
  }): Promise<void> {
    return await fetchJson('api/1/feature-flags/update', {
      method: HTTPMethod.post,
      body: asciiSafeStringify(params),
    })
  }

  public static async switchNativeAccount(hashid: string): Promise<{ redirect_url: string }> {
    return await fetchJson(`/auth/switch-native-account/${hashid}`, {
      method: HTTPMethod.post,
    })
  }
}

class Tenant {
  public static async updateTenantInfo(id: TenantId, fetchOptions: Partial<TenantParams>): Promise<TenantType> {
    const body: string = asciiSafeStringify(fetchOptions)
    return await fetchJson(`/tenants/${id}`, {
      method: HTTPMethod.put,
      body,
    })
  }

  public static async isSubdomainValid(subdomain: string): Promise<DomainValidation> {
    return await fetchJson(
      transformCurrentUrl(
        {},
        {
          path: '/tenants/subdomain_valid',
          search: { subdomain },
        },
      ),
      {
        method: HTTPMethod.get,
      },
    )
  }
}

class TenantInviteLinks {
  public static async get(id: TenantId): Promise<JsonAPIResponse<TenantInviteLinksType>> {
    return await fetchJson(`/api/tenants/${id}/invite_links`, {
      method: HTTPMethod.get,
    })
  }

  public static async patch(
    id: TenantId,
    fetchOptions: JsonAPIUpdateRequest<Partial<TenantInviteLinksParams>>,
  ): Promise<JsonAPIResponse<TenantInviteLinksType>> {
    const body: string = asciiSafeStringify(fetchOptions)
    return await fetchJson(`/api/tenants/${id}/invite_links`, {
      method: HTTPMethod.patch,
      body,
    })
  }
}

class TenantPermissions {
  public static async fetch(id: TenantId): Promise<JsonAPIResponse<TenantType>> {
    return await fetchJson(`/api/1/tenants/${id}/permissions`, {
      method: HTTPMethod.get,
    })
  }

  public static async update(id: TenantId, tenant: Partial<TenantType>): Promise<JsonAPIResponse<TenantType>> {
    return await fetchJson(`/api/1/tenants/${id}/permissions`, {
      method: HTTPMethod.patch,
      body: asciiSafeStringify({ data: { type: 'tenant', id: tenant.id, attributes: tenant } }),
    })
  }
}

class TenantAnalytics {
  public static async fetch(id: TenantId): Promise<JsonAPIResponse<TenantAnalyticsType>> {
    return await fetchJson(`/api/${API_VERSION}/tenants/${id}/analytics`, {
      method: HTTPMethod.get,
    })
  }
}

class LibraryAnalytics {
  public static async fetch(id: TenantId): Promise<JsonAPIResponse<LibraryAnalyticsType>> {
    return await fetchJson(`/api/${API_VERSION}/libraries/${id}/analytics`, {
      method: HTTPMethod.get,
    })
  }

  // v10 metrics endpoint URL. `metrics` are snake_case metric names; omitting the
  // option returns every metric. Unknown names are a 400, as is an empty list
  // (deliberately not treated as "fetch everything"). Shared with the polling
  // composable so the request URL is built in exactly one place.
  public static metricsUrl(
    id: TenantId,
    options: { metrics?: readonly LibraryAnalyticsMetricName[]; fromDate?: string; toDate?: string } = {},
  ): string {
    const params = new URLSearchParams()
    if (options.fromDate != null) params.set('from_date', options.fromDate)
    if (options.toDate != null) params.set('to_date', options.toDate)
    if (options.metrics != null) params.set('metrics', options.metrics.join(','))
    const query = params.toString()
    return `/api/10/libraries/${id}/analytics${query === '' ? '' : `?${query}`}`
  }

  public static async fetchMetrics(
    id: TenantId,
    options: { metrics?: readonly LibraryAnalyticsMetricName[]; fromDate?: string; toDate?: string } = {},
  ): Promise<JsonAPIResponse<LibraryAnalyticsMetricsType>> {
    return await fetchJson(LibraryAnalytics.metricsUrl(id, options), {
      method: HTTPMethod.get,
    })
  }

  // v10 per-user analytics (the Users tab). Server-side paginated/sortable/
  // searchable. The query string is built once so the table fetch and the CSV
  // download link stay in lockstep.
  private static usersQuery(options: LibraryUsersQuery): string {
    const params = new URLSearchParams()
    if (options.page != null) params.set('page', String(options.page))
    if (options.perPage != null) params.set('per_page', String(options.perPage))
    if (options.sort != null) params.set('sort', options.sort)
    if (options.order != null) params.set('order', options.order)
    if (options.q != null && options.q !== '') params.set('q', options.q)
    if (options.roles != null) options.roles.forEach((role) => role !== '' && params.append('role[]', role))
    if (options.fromDate != null) params.set('from_date', options.fromDate)
    if (options.toDate != null) params.set('to_date', options.toDate)
    if (options.locale != null && options.locale !== '') params.set('locale', options.locale)
    if (options.timezone != null && options.timezone !== '') params.set('timezone', options.timezone)
    const query = params.toString()
    return query === '' ? '' : `?${query}`
  }

  public static usersUrl(id: TenantId, options: LibraryUsersQuery = {}): string {
    return `/api/10/libraries/${id}/analytics/users${LibraryAnalytics.usersQuery(options)}`
  }

  public static usersCsvUrl(id: TenantId, options: LibraryUsersQuery = {}): string {
    return `/api/10/libraries/${id}/analytics/users/csv${LibraryAnalytics.usersQuery(options)}`
  }
}

class LibraryTimePeriods {
  private static fromApi(raw: Record<string, unknown>): LibraryTimePeriod {
    return {
      id: raw.id as string,
      // Billing terms carry no label or terms; default rather than cast, so a
      // consumer that reads them without checking source can't throw.
      label: (raw.label as string) ?? '',
      source: raw.source as LibraryTimePeriod['source'],
      startDate: raw.start_date as string,
      endDate: raw.end_date as string,
      terms: ((raw.terms as Record<string, unknown>[]) ?? []).map((t) => ({
        id: t.id as string,
        title: t.title as string,
        startDate: t.start_date as string,
        endDate: t.end_date as string,
        enabled: t.enabled as boolean,
      })),
    }
  }

  private static toApi(period: LibraryTimePeriod): Record<string, unknown> {
    return {
      id: period.id || undefined,
      label: period.label,
      start_date: period.startDate,
      end_date: period.endDate,
      terms: period.terms.map((t) => ({
        id: t.id || undefined,
        title: t.title,
        start_date: t.startDate,
        end_date: t.endDate,
        enabled: t.enabled,
      })),
    }
  }

  public static async fetch(libraryId: LibraryId): Promise<LibraryTimePeriod[]> {
    const response = await fetchJson(`/api/10/libraries/${libraryId}/time-periods`, {
      method: HTTPMethod.get,
    })
    const raw = (response?.data?.attributes?.periods ?? []) as Record<string, unknown>[]
    return raw.map(LibraryTimePeriods.fromApi)
  }

  public static async save(libraryId: LibraryId, periods: LibraryTimePeriod[]): Promise<LibraryTimePeriod[]> {
    const response = await fetchJson(`/api/10/libraries/${libraryId}/time-periods`, {
      method: HTTPMethod.put,
      jsonData: {
        data: {
          attributes: { periods: periods.map(LibraryTimePeriods.toApi) },
        },
      },
    })
    const raw = (response?.data?.attributes?.periods ?? []) as Record<string, unknown>[]
    return raw.map(LibraryTimePeriods.fromApi)
  }
}

class Lti {
  public static async generateLtiDynamicRegistrationUrl(id: TenantId): Promise<{ url: string; tenantId: number }> {
    return await fetchJson(`/api/lti/dynamic_registration_url?id=${id}`, {
      method: HTTPMethod.get,
    })
  }

  public static async generateLtiLinkDeploymentToken(id: TenantId): Promise<{ token: string; tenantId: number }> {
    return await fetchJson(`/api/lti/link_deployment_token?id=${id}`, {
      method: HTTPMethod.get,
    })
  }

  public static async updateLtiUserGroupSyncEnabled(
    tenantId: TenantId,
    enabled: boolean,
  ): Promise<JsonAPIResponse<LibraryType>> {
    return await fetchJson(`/api/lti/settings/user-group-sync?id=${tenantId}`, {
      method: HTTPMethod.patch,
      body: asciiSafeStringify({ enabled }),
    })
  }
}

class LibraryLti {
  public static async generateLtiDynamicRegistrationUrl(
    libraryId: LibraryId,
  ): Promise<{ url: string; libraryId: number }> {
    return await fetchJson(`/api/library-lti/dynamic-registration-url?id=${libraryId}`, {
      method: HTTPMethod.get,
    })
  }

  public static async generateLtiLinkDeploymentToken(
    libraryId: LibraryId,
  ): Promise<{ token: string; organizationType: string; organizationId: number }> {
    return await fetchJson(`/api/library-lti/link-deployment-token?id=${libraryId}`, {
      method: HTTPMethod.get,
    })
  }

  public static async updateLtiUserGroupSyncEnabled(
    libraryId: LibraryId,
    enabled: boolean,
  ): Promise<JsonAPIResponse<LibraryType>> {
    return await fetchJson(`/api/library-lti/settings/user-group-sync?id=${libraryId}`, {
      method: HTTPMethod.patch,
      body: asciiSafeStringify({ enabled }),
    })
  }

  public static async updateLtiDefaultGradebookEnabled(
    libraryId: LibraryId,
    enabled: boolean,
  ): Promise<JsonAPIResponse<LibraryType>> {
    return await fetchJson(`/api/library-lti/settings/default-gradebook?id=${libraryId}`, {
      method: HTTPMethod.patch,
      body: asciiSafeStringify({ enabled }),
    })
  }

  public static async updateDistrictSchoolLmsAccessEnabled(
    libraryId: LibraryId,
    enabled: boolean,
  ): Promise<JsonAPIResponse<LibraryType>> {
    return await fetchJson(`/api/library-lti/settings/lms-access?id=${libraryId}`, {
      method: HTTPMethod.patch,
      body: asciiSafeStringify({ enabled }),
    })
  }

  public static async generateGoogleClassroomDeploymentToken(
    libraryId: LibraryId,
  ): Promise<{ token: string; organizationType: string; organizationId: number }> {
    return await fetchJson(`/api/google-classroom/link-deployment-token?library_id=${libraryId}`, {
      method: HTTPMethod.get,
    })
  }
}

class NotificationsSettings {
  public static async fetch(): Promise<JsonAPIResponse<NotificationSettingApiResponse>> {
    return await fetchJson(`/api/${API_VERSION}/notification_settings/general`, {
      method: HTTPMethod.get,
    })
  }

  public static async update(fetchOptions = {}): Promise<JsonAPIResponse<NotificationSettingApiResponse>> {
    return await fetchJson(`/api/${API_VERSION}/notification_settings/general`, {
      method: HTTPMethod.patch,
      ...fetchOptions,
    })
  }
}

class UserFollows {
  public static async fetch({
    pageNumber,
    pageSize,
  }: {
    pageNumber: number
    pageSize: number
  }): Promise<JsonAPIResponse<UserFollowApiResponse>> {
    return await fetchJson(
      transformCurrentUrl(
        {},
        {
          path: `/api/${API_VERSION}/notification_settings/user_follows`,
          search: { 'page[size]': String(pageSize), 'page[number]': String(pageNumber) },
        },
      ),
      {
        method: HTTPMethod.get,
      },
    )
  }

  public static async create(fetchOptions = {}): Promise<{ success: boolean }> {
    return await fetchJson('/api/user_follows.json', {
      method: HTTPMethod.post,
      ...fetchOptions,
    })
  }

  public static async delete(fetchOptions = {}): Promise<{ success: boolean }> {
    return await fetchJson('/api/user_follows.json', {
      method: HTTPMethod.delete,
      ...fetchOptions,
    })
  }
}

class WallFollows {
  public static async fetch({
    pageNumber,
    pageSize,
  }: {
    pageNumber: number
    pageSize: number
  }): Promise<JsonAPIResponse<WallFollowApiResponse>> {
    return await fetchJson(
      transformCurrentUrl(
        {},
        {
          path: `/api/${API_VERSION}/notification_settings/wall_follows`,
          search: { 'page[size]': String(pageSize), 'page[number]': String(pageNumber) },
        },
      ),
      {
        method: HTTPMethod.get,
      },
    )
  }

  public static async create(fetchOptions = {}): Promise<JsonAPIResponse<WallFollowApiResponse>> {
    return await fetchJson(`/api/${API_VERSION}/notification_settings/wall_follows`, {
      method: HTTPMethod.post,
      ...fetchOptions,
    })
  }

  public static async update(
    { wallFollowId }: { wallFollowId: Id },
    fetchOptions = {},
  ): Promise<JsonAPIResponse<WallFollowApiResponse>> {
    return await fetchJson(`/api/${API_VERSION}/notification_settings/wall_follows/${String(wallFollowId)}`, {
      method: HTTPMethod.patch,
      ...fetchOptions,
    })
  }
}

class Wall {
  public static async archive(wallId: number): Promise<JsonAPIResponse<WallType>> {
    return await fetchJson(`/api/${API_VERSION}/walls/${wallId}/archive`, {
      method: HTTPMethod.put,
    })
  }

  public static async unarchive(wallId: number): Promise<JsonAPIResponse<WallType>> {
    return await fetchJson(`/api/${API_VERSION}/walls/${wallId}/unarchive`, {
      method: HTTPMethod.put,
    })
  }

  public static async delete(wallId: number): Promise<JsonAPIResponse<WallType>> {
    return await fetchJson(`/api/${API_VERSION}/walls/${wallId}`, {
      method: HTTPMethod.delete,
    })
  }

  public static async trash(wallId: WallId): Promise<JsonAPIResponse<WallType>> {
    return await fetchJson(`/api/${API_VERSION}/walls/${wallId}/trash`, {
      method: HTTPMethod.patch,
      headers: { 'Content-Length': '0' },
      body: '',
    })
  }

  public static async untrash(wallId: WallId): Promise<JsonAPIResponse<WallType>> {
    return await fetchJson(`/api/${API_VERSION}/walls/${wallId}/untrash`, {
      method: HTTPMethod.patch,
      headers: { 'Content-Length': '0' },
      body: '',
    })
  }

  public static async permanentlyDelete(wallId: WallId): Promise<JsonAPIResponse<WallType>> {
    return await fetchJson(`/api/8/walls/${wallId}/delete`, {
      method: HTTPMethod.delete,
    })
  }

  public static async removeFromRecents(wallId: WallId): Promise<Response> {
    return await fetchResponse(`/api/1/walls/${wallId}/remove_from_recents`, {
      method: HTTPMethod.delete,
      headers: {
        'X-CSRF-Token': getCsrfToken(),
      },
    })
  }

  public static async removeBookmark(params: {
    folderId: FolderId | null
    wallId: WallId
  }): Promise<JsonAPIResponse<WallType>> {
    return await fetchJson('/api/1/bookmarks', {
      method: HTTPMethod.delete,
      jsonData: params,
    })
  }

  public static async addBookmark(params: {
    folderId: FolderId | null
    wallId: WallId
  }): Promise<JsonAPIResponse<WallType>> {
    return await fetchJson('/api/1/bookmarks', {
      method: HTTPMethod.post,
      jsonData: params,
    })
  }

  public static async transfer(params: { wallId: WallId; destinationLibraryId: LibraryId | null }): Promise<void> {
    return await fetchJson('api/1/walls/transfer', {
      method: HTTPMethod.post,
      jsonData: params,
    })
  }
}

class WallTemplate {
  public static async fetchWallTemplates(libraryId?: LibraryId): Promise<JsonAPIResponse<WallTemplate>> {
    const searchParams: Record<string, string> = {}
    if (libraryId !== undefined) {
      searchParams.library_id = String(libraryId)
    }
    const url = transformCurrentUrl(
      {},
      {
        path: `/api/${API_VERSION}/walls/templates`,
        search: searchParams,
      },
    )
    return await fetchJson(url, {
      method: HTTPMethod.get,
    })
  }
}

class DashStartingStateApi {
  public static async fetchGalleryTemplates(): Promise<JsonAPIResponse<WallGalleryTemplate>> {
    return await fetchJson(`/api/${API_VERSION}/dashboard/starting-state/gallery-templates`, {
      method: HTTPMethod.get,
    })
  }

  public static async fetchSuggestedTemplatesData(): Promise<JsonAPIResponse<WallSuggestedTemplatesData>> {
    return await fetchJson(`/api/${API_VERSION}/dashboard/starting-state/gallery-templates/curated`, {
      method: HTTPMethod.get,
    })
  }

  public static async fetchPadletLearnVideos(): Promise<JsonAPIResponse<LearnVideo[]>> {
    return await fetchJson(`/api/${API_VERSION}/dashboard/starting-state/learn-videos`, {
      method: HTTPMethod.get,
    })
  }
}

class Folder {
  public static async fetch(): Promise<JsonAPIResponse<FolderApiResponse>> {
    return await fetchJson(`/api/${API_VERSION}/folders`, {
      method: HTTPMethod.get,
    })
  }

  public static async create(attributes: Pick<FolderType, 'name'>): Promise<JsonAPIResponse<FolderType>> {
    return await fetchJson(`/api/${API_VERSION}/folders`, {
      method: HTTPMethod.post,
      jsonData: {
        data: {
          type: 'folder',
          attributes,
        },
      },
    })
  }

  public static async delete(folderId: FolderType['id']): Promise<JsonAPIResponse<FolderType>> {
    return await fetchJson(`/api/${API_VERSION}/folders/${folderId}`, {
      method: HTTPMethod.delete,
    })
  }

  public static async update(
    folderId: FolderType['id'],
    attributes: Pick<FolderType, 'name'>,
  ): Promise<JsonAPIResponse<FolderType>> {
    return await fetchJson(`/api/${API_VERSION}/folders/${folderId}`, {
      method: HTTPMethod.patch,
      jsonData: {
        data: {
          type: 'folder',
          attributes,
        },
      },
    })
  }
}

class EmailAddressVerification {
  public static async validateEmailAndSendVerificationCode(email: string): Promise<Response> {
    return await fetchResponse('/api/auth/signup/check_email', {
      method: HTTPMethod.get,
      query: {
        email,
        change_email: '1',
      },
    })
  }

  public static async verifyEmailAddress(
    email: string,
    verificationCode: string,
    purpose: VerificationCodePurpose,
  ): Promise<EmailAddressVerificationResult> {
    return await fetchJson(`/api/${API_VERSION}/auth/email/verify`, {
      method: HTTPMethod.post,
      jsonData: {
        email_address: email,
        verification_code: verificationCode,
        purpose,
      },
    })
  }

  public static async resendVerificationCode(
    email: string,
    purpose: VerificationCodePurpose,
  ): Promise<EmailAddressVerificationResult> {
    return await fetchJson(`/api/${API_VERSION}/auth/email/resend_verification_code`, {
      method: HTTPMethod.post,
      jsonData: {
        email_address: email,
        purpose,
      },
    })
  }

  // The endpoint 404s with NO_USER both when no account exists and when the
  // account is invited-but-unregistered; either way there is no account
  // that can log in, so both map to false. Any other error rethrows.
  public static async checkEmailHasAccount(emailOrUsername: string): Promise<boolean> {
    try {
      await fetchResponse(`/api/${API_VERSION}/auth/check-if-can-login`, {
        method: HTTPMethod.get,
        query: { email_or_username: emailOrUsername },
      })
      return true
    } catch (e) {
      if (e?.status === HttpCode.NotFound) {
        try {
          const errorCode = JSON.parse(e.message)?.errors?.[0]?.code
          if (errorCode === ApiErrorCode.NO_USER) return false
        } catch {
          // Fall through to rethrow the original error.
        }
      }
      throw e
    }
  }
}

class UserGroups {
  public static async fetchUserGroupFolders(
    libraryId?: LibraryId,
  ): Promise<JsonAPIResponse<UserGroupFolderApiResponse>> {
    const fetchOptions: any = {}
    if (libraryId != null) fetchOptions.query = { libraryId }
    return await fetchJson('/api/1/group-folders', {
      method: HTTPMethod.get,
      ...fetchOptions,
    })
  }

  public static async updateUserGroup(
    userGroupId: number,
    attributes: UpdateUserGroupAttributes,
  ): Promise<JsonAPIResponse<UserGroupDetailsApiResponse>> {
    // Validate at least one field is being updated
    if (Object.keys(attributes).length === 0) {
      throw new Error('At least one attribute must be provided to update')
    }

    return await fetchJson(`/api/1/user-groups/${userGroupId}`, {
      method: HTTPMethod.patch,
      jsonData: {
        data: {
          attributes,
        },
      },
    })
  }

  public static async fetchUserGroup(userGroupId: number): Promise<JsonAPIResponse<UserGroupDetailsApiResponse>> {
    return await fetchJson(`/api/1/user-groups/${userGroupId}`, {
      method: HTTPMethod.get,
    })
  }

  public static async fetchUserGroupMembers(userGroupId: number): Promise<JsonAPIResponse<UserGroupMemberApiResponse>> {
    return await fetchJson(`/api/1/user-groups/${userGroupId}/members`, {
      method: HTTPMethod.get,
    })
  }

  public static async removeUserGroupMember(
    userGroupId: number,
    memberId: number,
  ): Promise<JsonAPIResponse<UserGroupMemberApiResponse>> {
    return await fetchJson(`/api/1/user-groups/${userGroupId}/members/${memberId}`, {
      method: HTTPMethod.delete,
    })
  }

  public static async addUserGroupMember(
    userGroupId: number,
    memberId: number,
  ): Promise<JsonAPIResponse<UserGroupMemberApiResponse>> {
    return await fetchJson(`/api/1/user-groups/${userGroupId}/members`, {
      method: HTTPMethod.post,
      jsonData: {
        member_id: memberId,
      },
    })
  }

  public static async searchLibraryMembers(
    libraryId: number,
    query: string,
    userGroupId?: number,
  ): Promise<JsonAPIResponse<UserGroupMemberApiResponse>> {
    const queryParams: Record<string, string> = {
      query,
      library_id: libraryId.toString(),
    }

    if (userGroupId !== undefined) {
      queryParams.user_group_id = userGroupId.toString()
    }

    return await fetchJson(`/api/1/user-group-memberships/search`, {
      method: HTTPMethod.get,
      query: queryParams,
    })
  }

  public static async archiveUserGroup(userGroupId: number): Promise<JsonAPIResponse<{ success: boolean }>> {
    return await fetchJson(`/api/1/user-groups/${userGroupId}/archive`, {
      method: HTTPMethod.post,
    })
  }

  public static async createUserGroup(
    libraryId: string,
    attributes: {
      name: string
      section?: string
      grade?: string
      subject?: string
      icon?: string
      memberIds?: number[]
      allowStudentAddPadlet?: boolean
      allowStudentCreateDiscussion?: boolean
    },
  ): Promise<JsonAPIResponse<UserGroupFolderApiResponse>> {
    return await fetchJson('/api/1/user-groups', {
      method: HTTPMethod.post,
      jsonData: {
        data: {
          attributes: {
            ...attributes,
            library_id: libraryId,
            member_ids: attributes.memberIds,
          },
        },
      },
    })
  }
}

class LibraryContentSafetySettings {
  public static async fetchContentSafetySettings(
    libraryId: string,
  ): Promise<JsonAPIResponse<ContentSafetySettingsPayload>> {
    return await fetchJson(`/api/${API_VERSION}/libraries/${libraryId}/content-safety-settings`, {
      method: HTTPMethod.get,
    })
  }

  public static async updateContentSafetySettings(
    libraryId: string,
    attributes: ContentSafetySettingsUpdateApiRequest,
  ): Promise<JsonAPIResponse<ContentSafetySettingsPayload>> {
    return await fetchJson(`/api/${API_VERSION}/libraries/${libraryId}/content-safety-settings`, {
      method: HTTPMethod.patch,
      jsonData: {
        data: {
          attributes,
        },
      },
    })
  }
}

class LibraryAttachmentTypeSettings {
  public static async fetchAttachmentTypeSettings(
    libraryId: LibraryId,
  ): Promise<JsonAPIResponse<AttachmentTypeSettingsPayload>> {
    return await fetchJson(`/api/${API_VERSION}/libraries/${libraryId}/attachment-type-settings`, {
      method: HTTPMethod.get,
    })
  }

  public static async updateAttachmentTypeSettings(
    libraryId: LibraryId,
    attributes: AttachmentTypeSettingsPayload,
  ): Promise<JsonAPIResponse<AttachmentTypeSettingsPayload>> {
    return await fetchJson(`/api/${API_VERSION}/libraries/${libraryId}/attachment-type-settings`, {
      method: HTTPMethod.put,
      jsonData: {
        data: {
          attributes,
        },
      },
    })
  }
}

class TenantContentSafetySettings {
  public static async fetchContentSafetySettings(
    tenantId: string,
  ): Promise<JsonAPIResponse<ContentSafetySettingsPayload>> {
    return await fetchJson(`/api/${API_VERSION}/tenants/${tenantId}/content-safety-settings`, {
      method: HTTPMethod.get,
    })
  }

  public static async updateContentSafetySettings(
    tenantId: string,
    attributes: ContentSafetySettingsUpdateApiRequest,
  ): Promise<JsonAPIResponse<ContentSafetySettingsPayload>> {
    return await fetchJson(`/api/${API_VERSION}/tenants/${tenantId}/content-safety-settings`, {
      method: HTTPMethod.patch,
      jsonData: {
        data: {
          attributes,
        },
      },
    })
  }
}

class GoogleAppLicensingSettings {
  public static async createLicenses(
    libraryId?: LibraryId,
  ): Promise<JsonAPIResponse<GoogleAppLicensingCreateResponse>> {
    return await fetchJson(`api/${API_VERSION}/google-app-licensing/create`, {
      method: HTTPMethod.post,
      jsonData: {
        libraryId,
      },
    })
  }

  public static async checkLicense(): Promise<JsonAPIResponse<GoogleAppLicensingValidateResponse>> {
    return await fetchJson(`api/${API_VERSION}/google-app-licensing/validate-or-logout`, {
      method: HTTPMethod.get,
    })
  }

  public static async checkDomainForExistingTenants(): Promise<JsonAPIResponse<string[]>> {
    return await fetchJson(`api/${API_VERSION}/google-app-licensing/existing-tenants`, {
      method: HTTPMethod.get,
    })
  }
}

class PaymentStatus {
  public static async fetchPaymentStatus(library_id?: string): Promise<JsonAPIResponse<PaymentStatusResponse>> {
    const query: any = {}
    if (library_id != null) {
      query.library_id = library_id
    }
    return await fetchJson(`/api/${API_VERSION}/billing/payment-status`, {
      method: HTTPMethod.get,
      query,
    })
  }
}

class Saml {
  public static async createSamlConnection(
    createAttributes: {
      idpMetadataUrl: string
      emailAttribute: string
      nameAttribute: string
      roleAttribute?: string
      adminRoles?: string[]
      connectionName?: string
      devMode?: boolean
      prespecifiedSpEntityId?: string
    },
    libraryId?: LibraryId,
    fetchOptions = {},
  ): Promise<JsonApiResponse<SamlConnectionApiResponse>> {
    const attributes = { ...createAttributes, libraryId }
    return await fetchJson('/api/1/saml-connections', {
      method: HTTPMethod.post,
      body: asciiSafeStringify({ data: { attributes } }),
      ...fetchOptions,
    })
  }

  public static async fetchSamlConnections(libraryId?: LibraryId): Promise<JsonApiResponse<SamlConnectionApiResponse>> {
    const fetchOptions: any = {}
    if (libraryId != null) fetchOptions.query = { libraryId }
    return await fetchJson('/api/1/saml-connections', {
      method: HTTPMethod.get,
      ...fetchOptions,
    })
  }

  public static async updateSamlConnection(
    samlIdpLinkId: Id,
    updateAttributes: {
      emailAttribute?: string
      nameAttribute?: string
      roleAttribute?: string
      adminRoles?: string[]
      connectionName?: string
      allowUpgradeToTeacherRole?: boolean
      devMode?: boolean
      idpMetadataUrl?: string
    },
    libraryId?: LibraryId,
  ): Promise<JsonAPIResponse<SchoolLibraryPermission>> {
    const attributes = { ...updateAttributes, libraryId }
    return await fetchJson(`/api/1/saml-connections/${samlIdpLinkId}`, {
      method: HTTPMethod.patch,
      body: asciiSafeStringify({ data: { attributes } }),
    })
  }
}

class ClassInformation {
  public static async fetchClassInformation(): Promise<JsonAPIResponse<ClassInformationType>> {
    return await fetchJson(`/api/${API_VERSION}/class-info`, {
      method: HTTPMethod.get,
    })
  }

  public static async upsert(classInformation: string): Promise<JsonAPIResponse<Response>> {
    return await fetchJson(`/api/${API_VERSION}/class-info`, {
      method: HTTPMethod.post,
      body: asciiSafeStringify({ class_info: classInformation }),
    })
  }
}

class TeacherSchoolProfile {
  // Routes register `1/teacher-school-profile` explicitly (not `:version`), so
  // pin to /api/1 instead of the global `API_VERSION` placeholder.
  public static async fetchProfile(): Promise<JsonAPIResponse<TeacherSchoolProfileType>> {
    return await fetchJson(`/api/1/teacher-school-profile`, {
      method: HTTPMethod.get,
    })
  }

  public static async upsert({
    school,
    grades,
    subjects,
  }: {
    school: TeacherSchoolType
    grades: string[]
    subjects: string[]
  }): Promise<void> {
    await fetchJson(`/api/1/teacher-school-profile`, {
      method: HTTPMethod.post,
      body: asciiSafeStringify({
        school,
        grades,
        subjects,
      }),
    })
  }

  // Records a "Skip for now" dismissal of the dashboard school flow prompt;
  // the server re-prompts after its skip cooldown elapses.
  public static async skip(): Promise<void> {
    await fetchJson(`/api/1/teacher-school-profile/skip`, {
      method: HTTPMethod.post,
    })
  }
}

// Records that the NPS survey was shown to or completed by the current user; the
// backend sets a cooldown (60 days for a show, 360 for a completion) before the
// survey is offered again, and -- for a completion with score 9 or 10 -- enqueues a
// review-request email. See NpsSurvey (Rails service) and nps_survey_store.ts
// (the Pinia store that calls this).
class NpsSurvey {
  public static async recordInteraction(action: 'shown' | 'completed', score?: number): Promise<void> {
    const payload = asciiSafeStringify({ interaction_type: action, score, authenticity_token: getCsrfToken() })
    const body = new Blob([payload], { type: 'application/json' })
    const queued = navigator.sendBeacon(`/api/1/recommendation-feedback/interaction`, body)
    if (!queued) {
      throw new Error('navigator.sendBeacon failed to queue the NPS survey interaction request')
    }
  }
}

import type {
  AnnouncementComment,
  AnnouncementCommentId,
  CampusAnnouncement,
  CampusAnnouncementId,
  CampusAnnouncementReaction,
  CampusDiscussion,
  DiscussionMessage,
  DiscussionMessageId,
  DiscussionMessagesPage,
  DiscussionsListPage,
  PollCreateParams,
  PollUpdateParams,
} from '@@/types'

class Discussions {
  /**
   * Fetch discussions for a given class id.
   */
  public static async listForClass(
    { classId }: { classId: number },
    fetchOptions: Partial<FetchOptions> = {},
  ): Promise<CampusDiscussion[]> {
    const response: JsonApiResponse<CampusDiscussion> = await fetchJson('/api/v1/discussions', {
      method: HTTPMethod.get,
      query: { class_id: String(classId) },
      ...fetchOptions,
    })
    const resourceList = Array.isArray(response.data) ? response.data : []
    const discussions = resourceList.map((resource: JsonApiData<CampusDiscussion>) => resource.attributes)
    return discussions
  }

  /**
   * Fetch paginated discussions for a given class id.
   */
  public static async listForClassPage(
    { classId, pageNumber = 0, pageSize = 20 }: { classId: number; pageNumber?: number; pageSize?: number },
    fetchOptions: Partial<FetchOptions> = {},
  ): Promise<DiscussionsListPage> {
    const response: JsonApiResponse<CampusDiscussion> = await fetchJson('/api/v1/discussions', {
      method: HTTPMethod.get,
      query: {
        class_id: String(classId),
        'page[size]': String(pageSize),
        'page[number]': String(pageNumber),
      },
      ...fetchOptions,
    })
    const resourceList = Array.isArray(response.data) ? response.data : []
    const items = resourceList.map((resource: JsonApiData<CampusDiscussion>) => resource.attributes)
    const hasMore = response.meta?.page?.hasNextPage ?? false
    return { items, hasMore }
  }

  /**
   * Create a discussion.
   */
  public static async createDiscussion(
    {
      classId,
      topic,
      description,
      attachmentUrl,
    }: {
      classId: number
      topic: string
      description?: string
      attachmentUrl?: string
    },
    fetchOptions: Partial<FetchOptions> = {},
  ): Promise<CampusDiscussion> {
    const response: JsonApiResponse<CampusDiscussion> = await fetchJson('/api/v1/discussions', {
      method: HTTPMethod.post,
      jsonData: {
        class_id: classId,
        topic,
        description,
        attachment_url: attachmentUrl,
      },
      ...fetchOptions,
    })
    const resource = Array.isArray(response.data) ? response.data[0] : response.data
    return (resource?.attributes ?? {}) as CampusDiscussion
  }

  /**
   * Update a discussion.
   */
  public static async updateDiscussion(
    {
      discussionId,
      topic,
      description,
      attachmentUrl,
    }: {
      discussionId: CampusDiscussionId
      topic?: string
      description?: string
      attachmentUrl?: string
    },
    fetchOptions: Partial<FetchOptions> = {},
  ): Promise<CampusDiscussion> {
    const response: JsonApiResponse<CampusDiscussion> = await fetchJson(`/api/v1/discussions/${discussionId}`, {
      method: HTTPMethod.patch,
      jsonData: {
        topic,
        description,
        attachment_url: attachmentUrl === undefined ? null : attachmentUrl,
      },
      ...fetchOptions,
    })
    const resource = Array.isArray(response.data) ? response.data[0] : response.data
    return (resource?.attributes ?? {}) as CampusDiscussion
  }

  /**
   * Fetch a single discussion (includes message counts and permissions).
   */
  public static async fetchDiscussion(
    { discussionId }: { discussionId: CampusDiscussionId },
    fetchOptions: Partial<FetchOptions> = {},
  ): Promise<CampusDiscussion> {
    const response: JsonApiResponse<CampusDiscussion> = await fetchJson(`/api/v1/discussions/${discussionId}`, {
      method: HTTPMethod.get,
      ...fetchOptions,
    })
    const resource = Array.isArray(response.data) ? response.data[0] : response.data
    return (resource?.attributes ?? {}) as CampusDiscussion
  }

  /**
   * Pin a discussion (sets pinned: true).
   */
  public static async pinDiscussion(
    { discussionId }: { discussionId: CampusDiscussionId },
    fetchOptions: Partial<FetchOptions> = {},
  ): Promise<CampusDiscussion> {
    const response: JsonApiResponse<CampusDiscussion> = await fetchJson(`/api/v1/discussions/${discussionId}/pin`, {
      method: HTTPMethod.patch,
      jsonData: { pinned: true },
      ...fetchOptions,
    })
    const resource = Array.isArray(response.data) ? response.data[0] : response.data
    return (resource?.attributes ?? {}) as CampusDiscussion
  }

  /**
   * Unpin a discussion (sets pinned: false).
   */
  public static async unpinDiscussion(
    { discussionId }: { discussionId: CampusDiscussionId },
    fetchOptions: Partial<FetchOptions> = {},
  ): Promise<CampusDiscussion> {
    const response: JsonApiResponse<CampusDiscussion> = await fetchJson(`/api/v1/discussions/${discussionId}/pin`, {
      method: HTTPMethod.patch,
      jsonData: { pinned: false },
      ...fetchOptions,
    })
    const resource = Array.isArray(response.data) ? response.data[0] : response.data
    return (resource?.attributes ?? {}) as CampusDiscussion
  }

  /**
   * Delete a discussion.
   */
  public static async deleteDiscussion(
    { discussionId }: { discussionId: CampusDiscussionId },
    fetchOptions: Partial<FetchOptions> = {},
  ): Promise<void> {
    await fetchJson(`/api/v1/discussions/${discussionId}`, {
      method: HTTPMethod.delete,
      ...fetchOptions,
    })
  }

  /**
   * Mark a discussion as viewed by the current user.
   * This persists the "seen" status and message view timestamp to the backend.
   */
  public static async markAsViewed(
    {
      discussionId,
      userId,
      oldestMessageViewedAt,
    }: {
      discussionId: CampusDiscussionId
      userId: number
      oldestMessageViewedAt: string
    },
    fetchOptions: Partial<FetchOptions> = {},
  ): Promise<void> {
    await fetchJson(`/api/v1/discussions/${discussionId}/mark-viewed`, {
      method: HTTPMethod.post,
      jsonData: {
        user_id: userId,
        oldest_message_viewed_at: oldestMessageViewedAt,
      },
      ...fetchOptions,
    })
  }
}

interface AnnouncementsListPage {
  items: CampusAnnouncement[]
  hasMore: boolean
}

class Announcements {
  /**
   * Fetch paginated announcements for a given class id.
   * Sorted by most recently created first.
   */
  public static async listForClassPage(
    { classId, pageNumber = 0, pageSize = 20 }: { classId: number; pageNumber?: number; pageSize?: number },
    fetchOptions: Partial<FetchOptions> = {},
  ): Promise<AnnouncementsListPage> {
    const response: JsonApiResponse<CampusAnnouncement> = await fetchJson('/api/v1/announcements', {
      method: HTTPMethod.get,
      query: {
        class_id: String(classId),
        'page[size]': String(pageSize),
        'page[number]': String(pageNumber),
      },
      ...fetchOptions,
    })
    const resourceList = Array.isArray(response.data) ? response.data : []
    const items = resourceList.map((resource: JsonApiData<CampusAnnouncement>) => resource.attributes)
    const hasMore = response.meta?.page?.hasNextPage ?? false
    return { items, hasMore }
  }

  /**
   * Create an announcement.
   * At least one of title, body, or attachmentUrl must be provided.
   */
  public static async createAnnouncement(
    {
      classId,
      title,
      body,
      attachmentUrl,
      allowComments,
      pinned,
      scheduledAt,
      originSessionId,
      poll,
    }: {
      classId: number
      title?: string
      body?: string
      attachmentUrl?: string
      allowComments?: boolean
      pinned?: boolean
      scheduledAt?: string | null
      originSessionId?: string
      poll?: PollCreateParams
    },
    fetchOptions: Partial<FetchOptions> = {},
  ): Promise<CampusAnnouncement> {
    const response: JsonApiResponse<CampusAnnouncement> = await fetchJson('/api/v1/announcements', {
      method: HTTPMethod.post,
      jsonData: {
        class_id: classId,
        title,
        body,
        attachment_url: attachmentUrl,
        allow_comments: allowComments,
        pinned,
        scheduled_at: scheduledAt,
        origin_session_id: originSessionId,
        poll,
      },
      ...fetchOptions,
    })
    const resource = Array.isArray(response.data) ? response.data[0] : response.data
    return (resource?.attributes ?? {}) as CampusAnnouncement
  }

  /**
   * Mark announcements for a class as viewed by the current user.
   * Used for "new" badge tracking.
   */
  public static async markAsViewed(
    { classId }: { classId: number },
    fetchOptions: Partial<FetchOptions> = {},
  ): Promise<void> {
    await fetchJson(`/api/v1/announcements/mark-viewed`, {
      method: HTTPMethod.post,
      jsonData: { class_id: classId },
      ...fetchOptions,
    })
  }

  /**
   * Delete an announcement.
   */
  public static async deleteAnnouncement(
    { announcementId }: { announcementId: CampusAnnouncementId },
    fetchOptions: Partial<FetchOptions> = {},
  ): Promise<void> {
    await fetchJson(`/api/v1/announcements/${announcementId}`, {
      method: HTTPMethod.delete,
      ...fetchOptions,
    })
  }

  /**
   * Update an announcement.
   */
  public static async updateAnnouncement(
    {
      announcementId,
      title,
      body,
      attachmentUrl,
      allowComments,
      scheduledAt,
      poll,
    }: {
      announcementId: CampusAnnouncementId
      title?: string
      body?: string
      attachmentUrl?: string | null
      allowComments?: boolean
      scheduledAt?: string | null
      poll?: PollUpdateParams | null
    },
    fetchOptions: Partial<FetchOptions> = {},
  ): Promise<CampusAnnouncement> {
    const response: JsonApiResponse<CampusAnnouncement> = await fetchJson(`/api/v1/announcements/${announcementId}`, {
      method: HTTPMethod.patch,
      jsonData: {
        title,
        body,
        attachment_url: attachmentUrl,
        allow_comments: allowComments,
        scheduled_at: scheduledAt,
        poll,
      },
      ...fetchOptions,
    })
    const resource = Array.isArray(response.data) ? response.data[0] : response.data
    return (resource?.attributes ?? {}) as CampusAnnouncement
  }

  /**
   * Pin an announcement (sets pinned: true).
   */
  public static async pinAnnouncement(
    { announcementId }: { announcementId: CampusAnnouncementId },
    fetchOptions: Partial<FetchOptions> = {},
  ): Promise<CampusAnnouncement> {
    const response: JsonApiResponse<CampusAnnouncement> = await fetchJson(
      `/api/v1/announcements/${announcementId}/pin`,
      {
        method: HTTPMethod.patch,
        jsonData: { pinned: true },
        ...fetchOptions,
      },
    )
    const resource = Array.isArray(response.data) ? response.data[0] : response.data
    return (resource?.attributes ?? {}) as CampusAnnouncement
  }

  /**
   * Unpin an announcement (sets pinned: false).
   */
  public static async unpinAnnouncement(
    { announcementId }: { announcementId: CampusAnnouncementId },
    fetchOptions: Partial<FetchOptions> = {},
  ): Promise<CampusAnnouncement> {
    const response: JsonApiResponse<CampusAnnouncement> = await fetchJson(
      `/api/v1/announcements/${announcementId}/pin`,
      {
        method: HTTPMethod.patch,
        jsonData: { pinned: false },
        ...fetchOptions,
      },
    )
    const resource = Array.isArray(response.data) ? response.data[0] : response.data
    return (resource?.attributes ?? {}) as CampusAnnouncement
  }

  /**
   * Vote on an announcement poll.
   */
  public static async voteOnPoll(
    {
      announcementId,
      pollChoiceId,
    }: {
      announcementId: CampusAnnouncementId
      pollChoiceId: number
    },
    fetchOptions: Partial<FetchOptions> = {},
  ): Promise<CampusAnnouncement> {
    const response: JsonApiResponse<CampusAnnouncement> = await fetchJson(
      `/api/v1/announcements/${announcementId}/poll-votes`,
      {
        method: HTTPMethod.post,
        jsonData: { poll_choice_id: pollChoiceId },
        ...fetchOptions,
      },
    )
    const resource = Array.isArray(response.data) ? response.data[0] : response.data
    return (resource?.attributes ?? {}) as CampusAnnouncement
  }
}

interface AnnouncementCommentsListResult {
  items: AnnouncementComment[]
  totalCount: number
}

/** Response shape for list endpoints that return an array of resources. */
interface AnnouncementCommentsListResponse {
  data: Array<JsonApiData<AnnouncementComment>>
  meta: { totalCount: number }
}

/** Response shape for single-resource endpoints. */
interface AnnouncementCommentResponse {
  data: JsonApiData<AnnouncementComment>
}

/**
 * API client for announcement comments.
 */
class AnnouncementComments {
  /**
   * List comments for an announcement.
   */
  public static async listForAnnouncement(
    { announcementId }: { announcementId: CampusAnnouncementId },
    fetchOptions: Partial<FetchOptions> = {},
  ): Promise<AnnouncementCommentsListResult> {
    const response: AnnouncementCommentsListResponse = await fetchJson('/api/v1/announcement_comments', {
      method: HTTPMethod.get,
      query: { announcement_id: announcementId },
      ...fetchOptions,
    })

    const items = response.data.map((resource) => resource.attributes)
    const totalCount = response.meta?.totalCount ?? items.length

    return { items, totalCount }
  }

  /**
   * Create a new comment on an announcement.
   */
  public static async createComment(
    {
      announcementId,
      content,
    }: {
      announcementId: CampusAnnouncementId
      content: string
    },
    fetchOptions: Partial<FetchOptions> = {},
  ): Promise<AnnouncementComment> {
    const jsonData: Record<string, unknown> = {
      announcement_id: announcementId,
      content,
    }
    const response: AnnouncementCommentResponse = await fetchJson('/api/v1/announcement_comments', {
      method: HTTPMethod.post,
      jsonData,
      ...fetchOptions,
    })
    return response.data.attributes
  }

  /**
   * Update a comment's content. For approve/reject use approveComment or rejectComment.
   */
  public static async updateComment(
    { commentId, content }: { commentId: AnnouncementCommentId; content: string },
    fetchOptions: Partial<FetchOptions> = {},
  ): Promise<AnnouncementComment> {
    const response: AnnouncementCommentResponse = await fetchJson(`/api/v1/announcement_comments/${commentId}`, {
      method: HTTPMethod.patch,
      jsonData: { content },
      ...fetchOptions,
    })
    return response.data.attributes
  }

  /**
   * Approve a pending comment.
   */
  public static async approveComment(
    { commentId }: { commentId: AnnouncementCommentId },
    fetchOptions: Partial<FetchOptions> = {},
  ): Promise<AnnouncementComment> {
    const response: AnnouncementCommentResponse = await fetchJson(
      `/api/v1/announcement_comments/${commentId}/approve`,
      {
        method: HTTPMethod.put,
        ...fetchOptions,
      },
    )
    return response.data.attributes
  }

  /**
   * Delete a comment.
   */
  public static async deleteComment(
    { commentId }: { commentId: AnnouncementCommentId },
    fetchOptions: Partial<FetchOptions> = {},
  ): Promise<void> {
    await fetchJson(`/api/v1/announcement_comments/${commentId}`, {
      method: HTTPMethod.delete,
      ...fetchOptions,
    })
  }
}

/**
 * API client for class-level operations (UserGroups used as campus classes).
 */
interface ClassRealtimeTokenResponse {
  brahmsToken: string
}

class Classes {
  /**
   * Fetch brahms token for class realtime channel connection.
   */
  public static async fetchRealtimeToken(
    { classId }: { classId: number },
    fetchOptions: Partial<FetchOptions> = {},
  ): Promise<ClassRealtimeTokenResponse> {
    const response: ClassRealtimeTokenResponse = await fetchJson(`/api/v1/classes/${classId}/realtime-token`, {
      method: HTTPMethod.get,
      ...fetchOptions,
    })
    return response
  }
}

class DiscussionMessages {
  /**
   * List messages for a discussion (paginated by cursor).
   */
  public static async listForDiscussion(
    {
      discussionId,
      afterCursorId,
      beforeCursorId,
      aroundMessageId,
      pageCount,
      pageSize,
      fromEnd,
      fromStart,
    }: {
      discussionId: CampusDiscussionId
      afterCursorId?: DiscussionMessageId
      beforeCursorId?: DiscussionMessageId
      aroundMessageId?: DiscussionMessageId
      pageCount?: number
      pageSize?: number
      fromEnd?: boolean
      fromStart?: boolean
    },
    fetchOptions: Partial<FetchOptions> = {},
  ): Promise<DiscussionMessagesPage> {
    const response: JsonApiResponse<DiscussionMessage> & { meta?: import('@@/types').DiscussionMessagesMeta } =
      await fetchJson('/api/v1/discussion_messages', {
        method: HTTPMethod.get,
        query: {
          discussion_id: String(discussionId),
          ...(afterCursorId != null ? { after_cursor_id: String(afterCursorId) } : {}),
          ...(beforeCursorId != null ? { before_cursor_id: String(beforeCursorId) } : {}),
          ...(aroundMessageId != null ? { around_message_id: String(aroundMessageId) } : {}),
          ...(pageCount != null ? { page_count: String(pageCount) } : {}),
          ...(pageSize != null ? { page_size: String(pageSize) } : {}),
          ...(fromEnd === true ? { from_end: 'true' } : {}),
          ...(fromStart === true ? { from_start: 'true' } : {}),
        },
        ...fetchOptions,
      })
    const resourceList = Array.isArray(response.data) ? response.data : []
    const items = resourceList.map((resource: JsonApiData<DiscussionMessage>) => resource.attributes)
    const nextCursorId = response.meta?.page?.nextCursorId ?? null
    const prevCursorId = response.meta?.page?.prevCursorId ?? null
    return { items, nextCursorId, prevCursorId }
  }

  /**
   * Create a new message in a discussion.
   */
  public static async createMessage(
    {
      discussionId,
      content,
      attachmentUrl,
      moderationStatus,
    }: { discussionId: CampusDiscussionId; content?: string; attachmentUrl?: string; moderationStatus?: string },
    fetchOptions: Partial<FetchOptions> = {},
  ): Promise<DiscussionMessage> {
    const response: JsonApiResponse<DiscussionMessage> = await fetchJson('/api/v1/discussion_messages', {
      method: HTTPMethod.post,
      jsonData: {
        discussion_id: discussionId,
        content,
        attachment_url: attachmentUrl,
        moderation_status: moderationStatus,
      },
      ...fetchOptions,
    })
    const resource = Array.isArray(response.data) ? response.data[0] : response.data
    return (resource?.attributes ?? {}) as DiscussionMessage
  }

  /**
   * Update a discussion message.
   */
  public static async updateMessage(
    {
      messageId,
      content,
      attachmentUrl,
      moderationStatus,
    }: { messageId: DiscussionMessageId; content?: string; attachmentUrl?: string; moderationStatus?: string },
    fetchOptions: Partial<FetchOptions> = {},
  ): Promise<DiscussionMessage> {
    const response: JsonApiResponse<DiscussionMessage> = await fetchJson(`/api/v1/discussion_messages/${messageId}`, {
      method: HTTPMethod.patch,
      jsonData: {
        content,
        attachment_url: attachmentUrl,
        moderation_status: moderationStatus,
      },
      ...fetchOptions,
    })
    const resource = Array.isArray(response.data) ? response.data[0] : response.data
    return (resource?.attributes ?? {}) as DiscussionMessage
  }

  /**
   * Delete a discussion message.
   */
  public static async deleteMessage(
    { messageId }: { messageId: DiscussionMessageId },
    fetchOptions: Partial<FetchOptions> = {},
  ): Promise<void> {
    await fetchJson(`/api/v1/discussion_messages/${messageId}`, {
      method: HTTPMethod.delete,
      ...fetchOptions,
    })
  }
}

class DiscussionMessageReactions {
  /**
   * Create a reaction to a discussion message.
   */
  public static async createReaction(
    {
      discussionMessageId,
      reactionType,
      value,
    }: { discussionMessageId: DiscussionMessageId; reactionType: string; value?: number | string },
    fetchOptions: Partial<FetchOptions> = {},
  ): Promise<void> {
    await fetchJson('/api/v1/discussion_message_reactions', {
      method: HTTPMethod.post,
      jsonData: {
        discussion_message_id: discussionMessageId,
        reaction_type: reactionType,
        value,
      },
      ...fetchOptions,
    })
  }

  /**
   * Update a reaction to a discussion message.
   */
  public static async updateReaction(
    { reactionId, reactionType, value }: { reactionId: string; reactionType?: string; value?: number | string },
    fetchOptions: Partial<FetchOptions> = {},
  ): Promise<void> {
    await fetchJson(`/api/v1/discussion_message_reactions/${reactionId}`, {
      method: HTTPMethod.patch,
      jsonData: {
        reaction_type: reactionType,
        value,
      },
      ...fetchOptions,
    })
  }

  /**
   * Delete a reaction to a discussion message.
   */
  public static async deleteReaction(
    { reactionId }: { reactionId: string },
    fetchOptions: Partial<FetchOptions> = {},
  ): Promise<void> {
    await fetchJson(`/api/v1/discussion_message_reactions/${reactionId}`, {
      method: HTTPMethod.delete,
      ...fetchOptions,
    })
  }

  /**
   * Get accumulated reactions for all messages in a discussion.
   */
  public static async getAccumulatedReactionsForDiscussion(
    { discussionId, userId }: { discussionId: CampusDiscussionId; userId?: number },
    fetchOptions: Partial<FetchOptions> = {},
  ): Promise<
    Record<
      DiscussionMessageId,
      {
        emojis: Record<string, number>
        userEmojiCode: string | null
      }
    >
  > {
    const params = new URLSearchParams({
      discussion_id: String(discussionId),
      ...(userId != null ? { user_id: String(userId) } : {}),
    })

    const response: {
      data: {
        attributes: {
          reactions: Record<
            DiscussionMessageId,
            {
              emojis: Record<string, number>
              userEmojiCode: string | null
            }
          >
        }
      }
    } = await fetchJson(
      `/api/v1/discussion_message_reactions/accumulated-reactions-for-discussion?${params.toString()}`,
      {
        method: HTTPMethod.get,
        ...fetchOptions,
      },
    )

    return response.data.attributes.reactions
  }

  /**
   * Get user IDs who reacted with a specific emoji to a discussion message.
   */
  public static async getUsersWhoReactedWithEmoji(
    {
      discussionMessageId,
      emojiCode,
      cursor,
      limit,
    }: { discussionMessageId: DiscussionMessageId; emojiCode: string; cursor?: string; limit?: number },
    fetchOptions: Partial<FetchOptions> = {},
  ): Promise<{ userIds: number[]; hasMore: boolean; nextCursor?: string }> {
    const params = new URLSearchParams({
      discussion_message_id: String(discussionMessageId),
      emoji_code: emojiCode,
      ...(cursor != null ? { cursor } : {}),
      ...(limit != null ? { limit: String(limit) } : {}),
    })

    const response: {
      data: {
        attributes: {
          userIds: number[]
          hasMore: boolean
          nextCursor?: string
        }
      }
    } = await fetchJson(`/api/v1/discussion_message_reactions/users-who-reacted-with-emoji?${params.toString()}`, {
      method: HTTPMethod.get,
      ...fetchOptions,
    })

    return response.data.attributes
  }
}

class CampusAnnouncementReactions {
  /**
   * Create a reaction to an announcement.
   */
  public static async createReaction(
    {
      announcementId,
      reactionType,
      value,
    }: { announcementId: CampusAnnouncementId; reactionType: string; value?: number | string },
    fetchOptions: Partial<FetchOptions> = {},
  ): Promise<CampusAnnouncementReaction> {
    const response: JsonApiResponse<CampusAnnouncementReaction> = await fetchJson('/api/v1/announcement_reactions', {
      method: HTTPMethod.post,
      jsonData: {
        announcement_id: announcementId,
        reaction_type: reactionType,
        value,
      },
      ...fetchOptions,
    })
    const resource = Array.isArray(response.data) ? response.data[0] : response.data
    return (resource?.attributes ?? {}) as CampusAnnouncementReaction
  }

  /**
   * Update a reaction to an announcement.
   */
  public static async updateReaction(
    { reactionId, reactionType, value }: { reactionId: string; reactionType?: string; value?: number | string },
    fetchOptions: Partial<FetchOptions> = {},
  ): Promise<CampusAnnouncementReaction> {
    const response: JsonApiResponse<CampusAnnouncementReaction> = await fetchJson(
      `/api/v1/announcement_reactions/${reactionId}`,
      {
        method: HTTPMethod.patch,
        jsonData: {
          reaction_type: reactionType,
          value,
        },
        ...fetchOptions,
      },
    )
    const resource = Array.isArray(response.data) ? response.data[0] : response.data
    return (resource?.attributes ?? {}) as CampusAnnouncementReaction
  }

  /**
   * Delete a reaction to an announcement.
   */
  public static async deleteReaction(
    { reactionId }: { reactionId: string },
    fetchOptions: Partial<FetchOptions> = {},
  ): Promise<void> {
    await fetchJson(`/api/v1/announcement_reactions/${reactionId}`, {
      method: HTTPMethod.delete,
      ...fetchOptions,
    })
  }

  /**
   * Get accumulated reactions for all announcements in a class.
   */
  public static async getAccumulatedReactionsForClass(
    { classId, userId }: { classId: number; userId?: number },
    fetchOptions: Partial<FetchOptions> = {},
  ): Promise<
    Record<
      CampusAnnouncementId,
      {
        emojis: Record<string, number>
        userEmojiCode: string | null
      }
    >
  > {
    const params = new URLSearchParams({
      class_id: String(classId),
      ...(userId != null ? { user_id: String(userId) } : {}),
    })

    const response: {
      data: {
        attributes: {
          reactions: Record<
            CampusAnnouncementId,
            {
              emojis: Record<string, number>
              userEmojiCode: string | null
            }
          >
        }
      }
    } = await fetchJson(`/api/v1/announcement_reactions/accumulated-reactions-for-class?${params.toString()}`, {
      method: HTTPMethod.get,
      ...fetchOptions,
    })

    return response.data.attributes.reactions
  }
}
// #endregion

class TenantTransferWalls {
  public static async startTransfer(
    srcUserId: Id,
    destUserId: Id,
    destLibraryId: LibraryId | undefined,
  ): Promise<{
    src_user_id: Id
    dest_user_id: Id
    dest_library_id: LibraryId | undefined
    started_at: number
    async_notification: boolean
  }> {
    const jsonData: {
      src_user_id: Id
      dest_user_id: Id
      dest_library_id?: LibraryId
    } = { src_user_id: srcUserId, dest_user_id: destUserId }

    if (destLibraryId != null) {
      jsonData.dest_library_id = destLibraryId
    }
    return await fetchJson('/api/1/transfer-walls', {
      method: HTTPMethod.post,
      jsonData,
    })
  }
}

class PersonalToLibraryTransferWalls {
  public static async transferWalls(
    libraryId: LibraryId,
  ): Promise<JsonAPIResponse<PersonalToLibraryTransferWallsResponse>> {
    return await fetchJson(`/api/${API_VERSION}/libraries/${libraryId}/transfer-personal-walls`, {
      method: HTTPMethod.post,
    })
  }

  public static async markTransferAsSeen(): Promise<JsonAPIResponse<Response>> {
    return await fetchJson(`/api/${API_VERSION}/transfer-walls/modal-seen`, {
      method: HTTPMethod.post,
    })
  }
}

class AiMakeSuggestionApi {
  private static readonly AI_API_VERSION = 1

  public static get url(): string {
    return `/api/${this.AI_API_VERSION}/ai-make-suggestions`
  }

  public static async fetchAiMakeSuggestions(): Promise<JsonAPIResponse<AiMakeSuggestion[]>> {
    return await fetchJson(this.url, {
      method: HTTPMethod.get,
    })
  }

  public static async generateAiMakeSuggestions(): Promise<JsonAPIResponse<AiMakeSuggestion[]>> {
    return await fetchJson(this.url, {
      method: HTTPMethod.post,
    })
  }

  public static async replaceAiMakeSuggestion(clickedId: string): Promise<JsonAPIResponse<AiMakeSuggestion[]>> {
    return await fetchJson(`${this.url}/replace`, {
      method: HTTPMethod.post,
      body: asciiSafeStringify({ clickedId }),
    })
  }

  public static async populateWhiteboard(hashid: string, suggestion: AiMakeSuggestion): Promise<void> {
    return await fetchJson(`${this.url}/populate-whiteboard/${hashid}`, {
      method: HTTPMethod.post,
      body: asciiSafeStringify({ suggestion }),
    })
  }
}

class CampusNewItemsCount {
  public static async fetch(
    { classIds }: { classIds: number[] },
    fetchOptions: Partial<FetchOptions> = {},
  ): Promise<number | null> {
    const params = new URLSearchParams()
    classIds.forEach((id) => params.append('class_ids[]', String(id)))
    const response: any = await fetchJson(`/api/v1/campus-new-items-count?${params.toString()}`, {
      method: HTTPMethod.get,
      ...fetchOptions,
    })
    return response?.data?.total_new_count ?? null
  }
}

class AiClassIconsApi {
  public static async suggest(
    name: string,
    fetchOptions: Partial<FetchOptions> = {},
  ): Promise<
    JsonAPIResponse<{
      emoji_url: string
    }>
  > {
    return await fetchJson(`/api/${API_VERSION}/ai-class-icons/suggest`, {
      method: HTTPMethod.post,
      jsonData: { name },
      ...fetchOptions,
    })
  }
}

class WhiteboardCardTemplateApi {
  public static async applyTemplate(wallHashid: string, templateType: string): Promise<void> {
    return await fetchJson(`/api/1/whiteboard/card-templates/apply/${wallHashid}`, {
      method: HTTPMethod.post,
      body: asciiSafeStringify({ template_type: templateType }),
    })
  }
}

class UserAchievements {
  public static async fetch(): Promise<JsonAPIResponse<{ url: string }>> {
    return await fetchJson(`/api/${API_VERSION}/user-achievements`, {
      method: HTTPMethod.get,
    })
  }

  public static async fetchStatus(key: string): Promise<
    JsonAPIResponse<{
      status: string
      data?: UserAchievementsType
    }>
  > {
    return await fetchJson(`/api/${API_VERSION}/user-achievements/status`, {
      method: HTTPMethod.get,
      query: { key },
    })
  }

  public static async fetchUserWallAnalytics(
    page: number,
    perPage: number,
  ): Promise<JsonAPIResponse<UserAchievementsAnalyticsWallContributionsType>> {
    return await fetchJson(`/api/${API_VERSION}/user-achievements/analytics/wall-contributions`, {
      method: HTTPMethod.get,
      query: {
        page: String(page),
        per_page: String(perPage),
      },
    })
  }

  public static async downloadCertificate(fetchOptions = {}): Promise<JsonAPIResponse<{ url: string }>> {
    return await fetchJson(`/api/${API_VERSION}/user-achievements/download-certificate`, {
      method: HTTPMethod.get,
      ...fetchOptions,
    })
  }

  public static async downloadBadge(fetchOptions = {}): Promise<JsonAPIResponse<{ url: string }>> {
    return await fetchJson(`/api/${API_VERSION}/user-achievements/download-badge`, {
      method: HTTPMethod.get,
      ...fetchOptions,
    })
  }
}

class PersonalTrial {
  public static async grant(fetchOptions = {}): Promise<{ data: { attributes: { redirect_url: string } } }> {
    return await fetchJson('/trial/grant', {
      method: HTTPMethod.post,
      ...fetchOptions,
    })
  }
}

class SelfServeTrialExperiment {
  public static async enroll(
    upgradeStep: string,
    fetchOptions = {},
  ): Promise<SelfServeTrialExperimentEnrollmentResponse> {
    return await fetchJson('/trial/enroll', {
      method: HTTPMethod.post,
      body: asciiSafeStringify({
        upgrade_step: upgradeStep,
      }),
      ...fetchOptions,
    })
  }
}

class Assignments {
  /**
   * Fetch paginated assignments for a given class id.
   * @param classId - The class (user group) ID
   * @param state - Filter by assignment state: 'draft', 'active', or 'closed'
   * @param includeDeliverables - Whether to include deliverable requirements in the response
   * @param pageNumber - Page number (0-indexed)
   * @param pageSize - Number of items per page
   */
  public static async listForClass(
    {
      classId,
      state,
      includeDeliverables: _includeDeliverables = true,
      pageNumber: _pageNumber = 0,
      pageSize: _pageSize = 20,
    }: {
      classId: number
      state?: AssignmentState
      includeDeliverables?: boolean
      pageNumber?: number
      pageSize?: number
    },
    fetchOptions: Partial<FetchOptions> = {},
  ): Promise<AssignmentsListPage> {
    const query: Record<string, string> = {
      user_group_id: String(classId),
    }
    if (state != null) {
      query.state = state
    }

    const response: JsonApiResponse<CampusAssignment> = await fetchJson('/api/v1/assignments', {
      method: HTTPMethod.get,
      query,
      ...fetchOptions,
    })
    const resourceList = Array.isArray(response.data) ? response.data : []
    const items = resourceList.map((resource: JsonApiData<CampusAssignment>) => resource.attributes)
    // Backend currently returns all items without pagination
    const hasMore = false
    return { items, hasMore }
  }

  /**
   * Fetch a single assignment by ID.
   */
  public static async get(
    { assignmentId, includeDeliverables = true }: { assignmentId: CampusAssignmentId; includeDeliverables?: boolean },
    fetchOptions: Partial<FetchOptions> = {},
  ): Promise<CampusAssignment> {
    const query: Record<string, string> = {}
    if (includeDeliverables) {
      query.include_deliverables = 'true'
    }

    const response: JsonApiResponse<CampusAssignment> = await fetchJson(`/api/v1/assignments/${assignmentId}`, {
      method: HTTPMethod.get,
      query,
      ...fetchOptions,
    })
    const resource = Array.isArray(response.data) ? response.data[0] : response.data
    return (resource?.attributes ?? {}) as CampusAssignment
  }

  /**
   * Create a new assignment (draft by default).
   * @param classId - The class (user group) ID
   * @param title - The assignment title
   */
  public static async create(
    {
      classId,
      title,
    }: {
      classId: number
      title: string
    },
    fetchOptions: Partial<FetchOptions> = {},
  ): Promise<CampusAssignment> {
    const response: JsonApiResponse<CampusAssignment> = await fetchJson('/api/v1/assignments', {
      method: HTTPMethod.post,
      body: asciiSafeStringify({
        assignment: {
          user_group_id: classId,
          title,
        },
      }),
      ...fetchOptions,
    })
    const resource = Array.isArray(response.data) ? response.data[0] : response.data
    return (resource?.attributes ?? {}) as CampusAssignment
  }

  /**
   * Update an existing assignment.
   * @param assignmentId - The assignment ID to update
   */
  public static async update(
    {
      assignmentId,
      title,
      instruction_html_body,
      grading_enabled,
      due_date_enabled,
      close_after_due_date,
      due_time,
      deliverable_requirements,
      props,
    }: {
      assignmentId: CampusAssignmentId
      title?: string
      instruction_html_body?: string
      grading_enabled?: boolean
      due_date_enabled?: boolean
      close_after_due_date?: boolean
      due_time?: string
      deliverable_requirements?: Array<{ templateType: string | null; templateId: number | null }>
      props?: Record<string, unknown>
    },
    fetchOptions: Partial<FetchOptions> = {},
  ): Promise<CampusAssignment> {
    const assignmentPayload: Record<string, unknown> = {}

    if (title !== undefined) assignmentPayload.title = title
    if (instruction_html_body !== undefined) assignmentPayload.instruction_html_body = instruction_html_body
    if (grading_enabled !== undefined) assignmentPayload.grading_enabled = grading_enabled
    if (due_date_enabled !== undefined) assignmentPayload.due_date_enabled = due_date_enabled
    if (close_after_due_date !== undefined) assignmentPayload.close_after_due_date = close_after_due_date
    if (due_time !== undefined) assignmentPayload.due_time = due_time
    if (props !== undefined) assignmentPayload.props = props
    if (deliverable_requirements !== undefined) {
      assignmentPayload.deliverable_requirements = deliverable_requirements.map((req) => ({
        template_type: req.templateType,
        template_id: req.templateId,
      }))
    }

    const response: JsonApiResponse<CampusAssignment> = await fetchJson(`/api/v1/assignments/${assignmentId}`, {
      method: HTTPMethod.patch,
      body: asciiSafeStringify({
        assignment: assignmentPayload,
      }),
      ...fetchOptions,
    })
    const resource = Array.isArray(response.data) ? response.data[0] : response.data
    return (resource?.attributes ?? {}) as CampusAssignment
  }

  /**
   * Publish an assignment (transition from draft to active state).
   * @param assignmentId - The assignment ID to publish
   */
  public static async publish(
    { assignmentId }: { assignmentId: CampusAssignmentId },
    fetchOptions: Partial<FetchOptions> = {},
  ): Promise<CampusAssignment> {
    const response: JsonApiResponse<CampusAssignment> = await fetchJson(`/api/v1/assignments/${assignmentId}/publish`, {
      method: HTTPMethod.patch,
      ...fetchOptions,
    })
    const resource = Array.isArray(response.data) ? response.data[0] : response.data
    return (resource?.attributes ?? {}) as CampusAssignment
  }

  /**
   * Delete an assignment.
   * @param assignmentId - The assignment ID to delete
   */
  public static async delete(
    { assignmentId }: { assignmentId: CampusAssignmentId },
    fetchOptions: Partial<FetchOptions> = {},
  ): Promise<void> {
    await fetchJson(`/api/v1/assignments/${assignmentId}`, {
      method: HTTPMethod.delete,
      ...fetchOptions,
    })
  }

  /**
   * Close an assignment (prevent further submissions).
   * @param assignmentId - The assignment ID to close
   */
  public static async close(
    { assignmentId }: { assignmentId: CampusAssignmentId },
    fetchOptions: Partial<FetchOptions> = {},
  ): Promise<CampusAssignment> {
    const response: JsonApiResponse<CampusAssignment> = await fetchJson(`/api/v1/assignments/${assignmentId}/close`, {
      method: HTTPMethod.patch,
      ...fetchOptions,
    })
    const resource = Array.isArray(response.data) ? response.data[0] : response.data
    return (resource?.attributes ?? {}) as CampusAssignment
  }

  /**
   * Reopen a closed assignment (allow submissions again).
   * @param assignmentId - The assignment ID to reopen
   */
  public static async reopen(
    { assignmentId }: { assignmentId: CampusAssignmentId },
    fetchOptions: Partial<FetchOptions> = {},
  ): Promise<CampusAssignment> {
    const response: JsonApiResponse<CampusAssignment> = await fetchJson(`/api/v1/assignments/${assignmentId}/reopen`, {
      method: HTTPMethod.patch,
      ...fetchOptions,
    })
    const resource = Array.isArray(response.data) ? response.data[0] : response.data
    return (resource?.attributes ?? {}) as CampusAssignment
  }
}

// #region AssignmentComments API
interface AssignmentCommentsListResponse {
  data: Array<{ attributes: AssignmentComment }>
  meta?: { totalCount: number }
}

interface AssignmentCommentResponse {
  data: { attributes: AssignmentComment }
}

interface AssignmentCommentsListResult {
  items: AssignmentComment[]
  totalCount: number
}

class AssignmentComments {
  /**
   * List comments for a submission.
   */
  public static async listForSubmission(
    { assignmentId, submissionId }: { assignmentId: string; submissionId: string },
    fetchOptions: Partial<FetchOptions> = {},
  ): Promise<AssignmentCommentsListResult> {
    const response: AssignmentCommentsListResponse = await fetchJson('/api/v1/assignment-comments', {
      method: HTTPMethod.get,
      query: { assignment_id: assignmentId, submission_id: submissionId },
      ...fetchOptions,
    })

    const items = response.data.map((resource) => resource.attributes)
    const totalCount = response.meta?.totalCount ?? items.length

    return { items, totalCount }
  }

  /**
   * Create a new comment on a submission.
   */
  public static async createComment(
    {
      assignmentId,
      submissionId,
      content,
      attachmentUrl,
    }: { assignmentId: string; submissionId: string; content: string; attachmentUrl?: string | null },
    fetchOptions: Partial<FetchOptions> = {},
  ): Promise<AssignmentComment> {
    const response: AssignmentCommentResponse = await fetchJson('/api/v1/assignment-comments', {
      method: HTTPMethod.post,
      jsonData: {
        assignment_id: assignmentId,
        submission_id: submissionId,
        content,
        attachment_url: attachmentUrl,
      },
      ...fetchOptions,
    })
    return response.data.attributes
  }

  /**
   * Update a comment.
   */
  public static async updateComment(
    {
      commentId,
      content,
      attachmentUrl,
    }: { commentId: AssignmentCommentId; content: string; attachmentUrl?: string | null },
    fetchOptions: Partial<FetchOptions> = {},
  ): Promise<AssignmentComment> {
    const response: AssignmentCommentResponse = await fetchJson(`/api/v1/assignment-comments/${commentId}`, {
      method: HTTPMethod.patch,
      jsonData: { content, attachment_url: attachmentUrl },
      ...fetchOptions,
    })
    return response.data.attributes
  }

  /**
   * Delete a comment.
   */
  public static async deleteComment(
    { commentId }: { commentId: AssignmentCommentId },
    fetchOptions: Partial<FetchOptions> = {},
  ): Promise<void> {
    await fetchJson(`/api/v1/assignment-comments/${commentId}`, {
      method: HTTPMethod.delete,
      ...fetchOptions,
    })
  }
}
// #endregion

export {
  AiClassIconsApi,
  AiMakeSuggestionApi,
  AnnouncementComments,
  Announcements,
  AssignmentComments,
  Assignments,
  BackpackPlan,
  CampusAnnouncementReactions,
  CampusNewItemsCount,
  Classes,
  ClassInformation,
  DashStartingStateApi,
  DiscussionMessageReactions,
  DiscussionMessages,
  Discussions,
  EmailAddressVerification,
  Folder,
  GoogleAppLicensingSettings,
  GoogleClassroom,
  GoogleWorkspace,
  Invoice,
  Library,
  LibraryAnalytics,
  LibraryAttachmentTypeSettings,
  LibraryBilling,
  LibraryContentSafetySettings,
  LibraryInfo,
  LibraryInvoice,
  LibraryLti,
  LibraryMembers,
  LibraryOauthSettings,
  LibraryPermissions,
  LibraryTimePeriods,
  LibraryUserEmailDomain,
  Lti,
  MicrosoftTeams,
  NotificationsSettings,
  NpsSurvey,
  PaymentStatus,
  PersonalToLibraryTransferWalls,
  PersonalTrial,
  Saml,
  SelfServeTrialExperiment,
  TeacherSchoolProfile,
  Tenant,
  TenantAnalytics,
  TenantContentSafetySettings,
  TenantInviteLinks,
  TenantPermissions,
  TenantTransferWalls,
  User,
  UserAchievements,
  UserFollows,
  UserGroups,
  Wall,
  WallFollows,
  WallTemplate,
  WhiteboardCardTemplateApi,
}
